/**
 * DataService - mirrors the existing SAP UI5 DataService.
 * Supports three modes controlled by VITE_DATA_SOURCE:
 *   - 'mock' (default): loads from /mockdata/ JSON files
 *   - 'api': fetches from /api/ REST endpoints
 *   - 'supabase': queries Supabase directly via the JS client
 * Legacy env var VITE_MOCK_DATA=false is still respected (â†' api mode).
 */

import type { Portfolio, Project, ProjectStatus, Risk, WbsElement, Task, Resource, BaselineModel, BaselineWbsSummary, BaselineTimePhaseEntry, BurdenRateDeck, ResourceCategory, ScheduleVersionSummary, BaselineChangeRequest, RevenuePlanEntry, Milestone } from '@/types/ppm'
import type { ProjectForecastConfig, ForecastVersion, ForecastWbsSummary, ForecastResourceLine, ForecastTimePhaseEntry, RevenueForecastLine, RevenueTimePhaseEntry, ActualsSyncRecord, EacCycle, EacSubmission, MaterialForecastLine, OdcForecastLine, OdcCategory, CapitalAssetForecastLine, WorkAuthorization } from '@/types/forecasting'

// Legacy JSONB-shape forecast submission row read from the seed JSON
// (forecasting/forecast-submissions.json). Predates the typed
// EacSubmission migration; kept for the legacy useForecastSubmissions
// hook in ForecastManagement which falls back to the JSONB feed when
// the typed table is empty.
interface ForecastSubmission {
  forecastVersionId: string
  [key: string]: unknown
}
import type { EvmProjectTimeSeries, EvmBaselineSnapshot, EvmPeriodicActual, EvtAssignment, ProgressEntry, EvmThresholdConfig, VarianceNarrative } from '@/types/evm'
import type { SapSystemConfig, SapIntegrationProfile, SapSyncJob, SyncDomain, SyncedActualsSummary, SapIntegrationDashboard, SapCostElementMaster, SapWbsElementMaster } from '@/types/sap-integration'
import type { LessonLearned } from '@/types/lessonsLearned'
import { supabase } from '@/lib/supabase'
import { useModuleEntitlementStore } from '@/config/moduleEntitlements'
import * as sb from '@/services/supabaseService'
import * as agile from '@/services/agileService'
import type { WorkItem, Board, Sprint, ProgramIncrement, WorkItemLink, WorkItemActivity, WorkItemComment, AgileMetricSnapshot } from '@/types/agile'
import * as sapSb from '@/services/sapSupabaseService'
import * as sapMock from '@/services/sapIntegrationService'
// SAP backend option (isolated side project; only active when VITE_DATA_SOURCE=sap).
// See services/sap-backend/README.md. Inert in every Supabase/mock/api deployment.
import { sapBackend } from '@/services/sap-backend'

// Determine data source: 'mock' | 'api' | 'supabase'
// The env var sets the *configured* backend. At runtime, demo mode always
// falls back to mock JSON regardless of the configured source, and non-demo
// modes (sandbox, dev, quality, production) always use Supabase.
type DataSource = 'mock' | 'api' | 'supabase' | 'sap'
function resolveConfiguredSource(): DataSource {
  const explicit = import.meta.env.VITE_DATA_SOURCE as string | undefined
  if (explicit === 'supabase') return 'supabase'
  if (explicit === 'sap') return 'sap' // SAP backend option (see sap-backend/)
  if (explicit === 'api') return 'api'
  if (explicit === 'mock') return 'mock'
  // Legacy fallback
  if (import.meta.env.VITE_MOCK_DATA === 'false') return 'api'
  return 'mock'
}

const CONFIGURED_SOURCE = resolveConfiguredSource()
const MOCK_BASE = '/mockdata'
const API_BASE = '/api'

/**
 * Demo mode detection â€" returns the active demo profile ID.
 * When VITE_DATA_SOURCE is 'supabase', demo data is served from Supabase
 * tables (seeded per profile via seed_*.sql files).
 * When VITE_DATA_SOURCE is 'mock', demo data falls back to profile JSON.
 */
function isDemoMode(): boolean {
  try {
    const mode = localStorage.getItem('ppm-app-mode')
    return mode === null || mode === 'demo'
  } catch { return false }
}

/** Active demo profile ID, or null when not in demo mode */
export function getActiveDemoProfile(): string | null {
  if (!isDemoMode()) return null
  try {
    const id = localStorage.getItem('ppm-demo-profile') ?? 'technology-digital'
    return id === 'default' ? 'technology-digital' : id
  } catch { return 'technology-digital' }
}

/**
 * Resolve the effective data source at runtime.
 * - Demo mode â†' respects VITE_DATA_SOURCE (supabase/api) if set, else mock JSON
 * - Non-demo modes (sandbox, dev, quality, production) â†' always supabase
 */
function resolveRuntimeSource(): DataSource {
  if (isDemoMode()) return CONFIGURED_SOURCE
  // Non-demo modes always use Supabase for real org-scoped data
  return CONFIGURED_SOURCE === 'mock' ? 'supabase' : CONFIGURED_SOURCE
}

function isMockMode(): boolean {
  return resolveRuntimeSource() === 'mock'
}

// SAP backend option. `sap` mode reads SAP for migrated entities (checked via
// isSapMode() at the top of each wired function) and falls back to the Supabase
// substrate for everything not yet migrated - hence isSupabaseMode() is true in
// sap mode too. In supabase/mock/api deployments isSapMode() is always false, so
// the live Supabase path is unaffected. See services/sap-backend/.
function isSapMode(): boolean {
  return resolveRuntimeSource() === 'sap'
}

function isSupabaseMode(): boolean {
  const src = resolveRuntimeSource()
  return src === 'supabase' || src === 'sap'
}

// ---------------------------------------------------------------------------
// Sandbox mode â€" user-generated data only, no simulated/demo seed rows.
// Filters list queries (projects, portfolios, master contracts) to exclude
// rows whose IDs match known demo seed patterns. User-created rows in
// sandbox use distinct IDs (timestamp-based or org-prefixed) and pass
// through.
// ---------------------------------------------------------------------------
function isSandboxMode(): boolean {
  try { return localStorage.getItem('ppm-app-mode') === 'sandbox' }
  catch { return false }
}

// Matches known demo seed IDs. Intentionally does NOT match user-created
// IDs that happen to start with port_<timestamp>, since `Date.now()` falls
// in the 17xxxxxxxxxxx range and would collide.
const DEMO_SEED_ID_PATTERN = /^(?:proj_(?:00\d|0\d{2}|mdc2)|projdemo_|port_(?:00\d|root|capital|defense_aero|env)$|port_(?:00\d|root|capital|defense_aero|env)_|portdemo_|mc_(?:00\d|demo_)|mcdemo_)/

export function isDemoSeedId(id: string): boolean {
  return DEMO_SEED_ID_PATTERN.test(id)
}

function filterOutDemoSeeds<T extends { id: string }>(rows: T[]): T[] {
  return isSandboxMode() ? rows.filter(r => !isDemoSeedId(r.id)) : rows
}

// Re-export for convenience
export { getCurrentOrganizationId } from '@/lib/orgContext'

const cache = new Map<string, unknown>()

// ---------------------------------------------------------------------------
// Demo profile support: resolve mock file path based on active profile
// ---------------------------------------------------------------------------
function getActiveDemoProfileDir(): string {
  try {
    const profileId = localStorage.getItem('ppm-demo-profile') ?? 'technology-digital'
    if (profileId === 'technology-digital' || profileId === 'default') return ''
    // Dynamic import would be circular; read directly from the known registry
    const profileDirs: Record<string, string> = {
      'engineering-construction': 'profiles/engineering-construction',
      'aerospace-defense': 'profiles/aerospace-defense',
    }
    return profileDirs[profileId] ?? ''
  } catch { return '' }
}

// ---------------------------------------------------------------------------
// Mock mode: load JSON files with caching
// ---------------------------------------------------------------------------
async function loadMock<T>(file: string): Promise<T> {
  const profileDir = getActiveDemoProfileDir()
  const resolvedFile = profileDir ? `${profileDir}/${file}` : file
  const cacheKey = resolvedFile

  const cached = cache.get(cacheKey)
  if (cached) return cached as T

  // Try profile-specific file first, fall back to default
  if (profileDir) {
    const profileRes = await fetch(`${MOCK_BASE}/${resolvedFile}`)
    if (profileRes.ok) {
      const data = await profileRes.json()
      cache.set(cacheKey, data)
      return data as T
    }
    // Fallback to default mockdata
    const fallbackRes = await fetch(`${MOCK_BASE}/${file}`)
    if (!fallbackRes.ok) throw new Error(`Failed to load: ${file}`)
    const data = await fallbackRes.json()
    cache.set(cacheKey, data)
    return data as T
  }

  const res = await fetch(`${MOCK_BASE}/${file}`)
  if (!res.ok) throw new Error(`Failed to load: ${file}`)
  const data = await res.json()
  cache.set(cacheKey, data)
  return data as T
}

// ---------------------------------------------------------------------------
// API mode: fetch from REST endpoints
// ---------------------------------------------------------------------------
async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession()
  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` }
  }
  return {}
}

async function fetchApi<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) url.searchParams.set(k, v)
    })
  }
  const headers = await getAuthHeaders()
  const res = await fetch(url.toString(), { headers })
  if (!res.ok) throw new Error(`API error ${res.status}: ${path}`)
  return res.json()
}

export function clearCache() {
  cache.clear()
}

/** Call when the demo profile changes to flush cached mock data */
export function clearMockCache() {
  cache.clear()
}

// ---------------------------------------------------------------------------
// Portfolios
// ---------------------------------------------------------------------------
export async function getPortfolios(): Promise<Portfolio[]> {
  if (isSupabaseMode()) return filterOutDemoSeeds(await sb.getPortfolios())
  if (!isMockMode()) return filterOutDemoSeeds(await fetchApi<Portfolio[]>('/portfolios'))
  const d = await loadMock<{ portfolios: Portfolio[] }>('portfolios.json')
  return d.portfolios ?? []
}

/** Portfolios across ALL orgs, for org DISCOVERY (the sandbox org picker's
 *  "which orgs own a portfolio?"). The scoped getPortfolios() can't answer that
 *  before an org is selected (sandbox + no org → organization_id IS NULL only).
 *  Demo-seed rows still filtered; non-supabase modes fall back to the scoped list. */
export async function getPortfoliosForOrgDiscovery(): Promise<Portfolio[]> {
  if (isSupabaseMode()) return filterOutDemoSeeds(await sb.getPortfoliosUnscoped())
  return getPortfolios()
}

export async function getPortfolioById(id: string): Promise<Portfolio | null> {
  if (isSupabaseMode()) return sb.getPortfolioById(id)
  if (!isMockMode()) {
    try { return await fetchApi<Portfolio>(`/portfolios/${id}`) }
    catch { return null }
  }
  const all = await getPortfolios()
  return all.find(p => p.id === id) ?? null
}

export async function getPortfolioHierarchy(): Promise<Portfolio[]> {
  // In sandbox mode, build the hierarchy from the filtered getPortfolios()
  // so demo seed rows don't leak in via sb.getPortfolioHierarchy().
  if (isSupabaseMode() && !isSandboxMode()) return sb.getPortfolioHierarchy()
  if (!isMockMode() && !isSupabaseMode()) return fetchApi<Portfolio[]>('/portfolios/hierarchy')
  const all = await getPortfolios()
  const byId = new Map<string, Portfolio & { children: Portfolio[] }>()
  all.forEach(p => byId.set(p.id, { ...p, children: [] }))

  const roots: Portfolio[] = []
  all.forEach(p => {
    const node = byId.get(p.id)!
    if (p.parentId && byId.has(p.parentId)) {
      byId.get(p.parentId)!.children.push(node)
    } else {
      roots.push(node)
    }
  })
  return roots
}

export async function createPortfolio(portfolio: Portfolio): Promise<void> {
  if (isSupabaseMode()) return sb.createPortfolio(portfolio)
  if (!isMockMode()) {
    const headers = await getAuthHeaders()
    const res = await fetch(`${API_BASE}/portfolios`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(portfolio),
    })
    if (!res.ok) throw new Error(`API error ${res.status}: POST /portfolios`)
    return
  }
  // Mock mode: add to in-memory cache
  const d = await loadMock<{ portfolios: Portfolio[] }>('portfolios.json')
  const all = d.portfolios ?? []
  all.push(portfolio)
  cache.set('portfolios.json', { portfolios: all })

  // Update parent's childIds in mock cache
  if (portfolio.parentId) {
    const parent = all.find(p => p.id === portfolio.parentId)
    if (parent && !parent.childIds.includes(portfolio.id)) {
      parent.childIds.push(portfolio.id)
    }
  }
}

/**
 * Patch update for a portfolio. Production-only against Supabase. Mock and
 * REST modes throw so callers don't silently no-op.
 */
export async function updatePortfolio(id: string, patch: Partial<Portfolio>): Promise<Portfolio> {
  if (isSupabaseMode()) return sb.updatePortfolio(id, patch)
  throw new Error('updatePortfolio requires Supabase mode (set VITE_DATA_SOURCE=supabase).')
}

export async function checkPortfolioCodeExists(code: string): Promise<boolean> {
  if (isSupabaseMode()) return sb.checkPortfolioCodeExists(code)
  // Mock/API: check against loaded portfolios
  const all = await getPortfolios()
  return all.some(p => p.code === code)
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
export async function getProjects(portfolioId?: string): Promise<Project[]> {
  if (isSapMode()) return sapBackend.getProjects(portfolioId)
  if (isSupabaseMode()) return filterOutDemoSeeds(await sb.getProjects(portfolioId))
  if (!isMockMode()) return filterOutDemoSeeds(await fetchApi<Project[]>('/projects', { portfolioId }))
  const d = await loadMock<{ projects: Project[] }>('projects.json')
  const all = d.projects ?? []
  return portfolioId ? all.filter(p => p.portfolioId === portfolioId) : all
}

export async function getProjectById(id: string): Promise<Project | null> {
  if (isSupabaseMode()) return sb.getProjectById(id)
  if (!isMockMode()) {
    try { return await fetchApi<Project>(`/projects/${id}`) }
    catch { return null }
  }
  const all = await getProjects()
  return all.find(p => p.id === id) ?? null
}

/**
 * Create a project in Draft status from the direct/indirect startup wizard.
 * Always writes to Supabase (migration 00240 â€" projects table accepts the
 * 'Draft' status string). Returns the assembled Project so the caller can
 * keep using the same camelCase shape it built.
 */
export async function createDraftProject(partial: Partial<Project> & Pick<Project, 'name'>): Promise<Project> {
  const now = new Date().toISOString().slice(0, 10)
  const project: Project = {
    id: partial.id ?? `proj_draft_${Date.now()}`,
    code: partial.code ?? `DRAFT-${Math.floor(Math.random() * 9000 + 1000)}`,
    name: partial.name,
    description: partial.description ?? '',
    portfolioId: partial.portfolioId ?? '',
    projectManager: partial.projectManager ?? '',
    projectManagerName: partial.projectManagerName ?? '',
    status: 'Draft',
    startDate: partial.startDate ?? now,
    plannedEndDate: partial.plannedEndDate ?? now,
    actualEndDate: null,
    budget: partial.budget ?? { total: 0, spent: 0, committed: 0, available: 0, currency: 'USD' },
    fiscalYearBudgets: partial.fiscalYearBudgets,
    schedule: partial.schedule ?? { percentComplete: 0, spi: 1, cpi: 1, scheduleVarianceDays: 0 },
    schedulingMethod: partial.schedulingMethod ?? 'traditional',
    deliveryMethodology: partial.deliveryMethodology ?? (partial.schedulingMethod === 'agile' ? 'agile' : 'waterfall'),
    healthStatus: 'Green',
    evmEnabled: partial.evmEnabled ?? false,
    riskScore: 0,
    businessOwner: partial.businessOwner ?? '',
    priority: partial.priority ?? 'Medium',
    projectType: partial.projectType ?? '',
    fundingType: partial.fundingType ?? 'External',
    contractId: partial.contractId ?? null,
    wbsElements: [],
    isCapitalAsset: partial.isCapitalAsset,
    eacCycleFrequency: partial.eacCycleFrequency ?? null,
    phase: partial.phase,
    lifecycleProfileId: partial.lifecycleProfileId ?? null,
    dataClass: partial.dataClass ?? 'production',
  }
  return sb.createDraftProject(project)
}

/** Flip a Draft project to its active state (Planning by default). */
export async function activateProject(projectId: string, status: ProjectStatus = 'Planning'): Promise<void> {
  await sb.updateProjectStatus(projectId, status)
}

/** Soft-archive or restore a project (sets/clears archived_at). */
export async function setProjectArchived(projectId: string, archived: boolean): Promise<void> {
  await sb.setProjectArchived(projectId, archived)
}

/** Align a project to an organization (localization). Pass null to clear. */
export async function setProjectOrganization(projectId: string, organizationId: string | null): Promise<void> {
  await sb.setProjectOrganization(projectId, organizationId)
}

// ---------------------------------------------------------------------------
// WBS
// ---------------------------------------------------------------------------
export async function getWbsElements(projectId?: string): Promise<WbsElement[]> {
  if (isSapMode()) return sapBackend.getWbsElements(projectId)
  if (isSupabaseMode()) return sb.getWbsElements(projectId)
  if (!isMockMode()) return fetchApi<WbsElement[]>('/wbs-elements', { projectId })
  const d = await loadMock<{ wbsElements: WbsElement[] }>('wbs-elements.json')
  const all = d.wbsElements ?? []
  return projectId ? all.filter(w => w.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------
export async function getTasks(wbsId?: string): Promise<Task[]> {
  if (isSupabaseMode()) return sb.getTasks(wbsId)
  if (!isMockMode()) return fetchApi<Task[]>('/tasks', { wbsElementId: wbsId })
  const d = await loadMock<{ tasks: Task[] }>('tasks.json')
  const all = d.tasks ?? []
  return wbsId ? all.filter(t => t.wbsElementId === wbsId) : all
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
export async function getUsers() {
  if (isSupabaseMode()) return sb.getUsers()
  if (!isMockMode()) return fetchApi<Array<{ id: string; name: string; email: string; title: string; department: string; organization: string; isPortfolioOwnerEligible?: boolean }>>('/users')
  const d = await loadMock<{ users: Array<{ id: string; name: string; email: string; title: string; department: string; organization: string; isPortfolioOwnerEligible?: boolean }> }>('users.json')
  return d.users ?? []
}

/**
 * Create a new user (HCM / personnel record). In Supabase mode this writes
 * to public.users; in mock/api mode this is a best-effort no-op that returns
 * the input (the Personnel Management page maintains a local override list).
 */
export async function createUser(input: {
  id?: string
  name: string
  email: string
  title?: string
  department?: string
  organization?: string
  primaryOrganizationId?: string | null
  isPortfolioOwnerEligible?: boolean
}) {
  if (isSupabaseMode()) return sb.createUser(input)
  // Mock / api modes: returns the shaped record. Persistence in mock mode
  // lives in the Personnel Management page (in-memory + localStorage).
  return { ...input, id: input.id || `user_${Math.random().toString(36).slice(2, 10)}` }
}

/**
 * Assign users to a tenant organization (Personnel Studio). Supabase-only
 * write; mock/api modes no-op and the page keeps the change in local state.
 */
export async function assignUsersToOrganization(
  userIds: string[],
  orgId: string | null,
  orgName: string | null,
) {
  if (isSupabaseMode()) return sb.assignUsersToOrganization(userIds, orgId, orgName)
}

/** All org membership rows (user_organization_access), Supabase-only. */
export async function getUserOrgMemberships(): Promise<sb.UserOrgMembership[]> {
  if (isSupabaseMode()) return sb.getUserOrgMemberships()
  return []
}

/** Add a membership in one org for several users (bulk), Supabase-only. */
export async function addUsersOrgMembership(userIds: string[], orgId: string) {
  if (isSupabaseMode()) return sb.addUsersOrgMembership(userIds, orgId)
}

/** Reconcile one user's memberships to exactly orgIds, Supabase-only. */
export async function setUserOrgMemberships(userId: string, orgIds: string[]) {
  if (isSupabaseMode()) return sb.setUserOrgMemberships(userId, orgIds)
}

/**
 * Toggle the HCM-level portfolio owner eligibility flag. The portfolio
 * owner picker is filtered by this flag, so changes here change which
 * users appear in that picker globally.
 */
export async function setUserPortfolioOwnerEligibility(userId: string, eligible: boolean) {
  if (isSupabaseMode()) return sb.setUserPortfolioOwnerEligibility(userId, eligible)
  return { id: userId, isPortfolioOwnerEligible: eligible }
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------
export async function getResources(): Promise<Resource[]> {
  if (isSapMode()) return sapBackend.getResources()
  if (isSupabaseMode()) return sb.getResources()
  if (!isMockMode()) return fetchApi<Resource[]>('/resources')
  const d = await loadMock<{ resources: Resource[] }>('resources.json')
  return d.resources ?? []
}

/**
 * Insert (or upsert on conflict) a Resource row.
 *
 * Supabase mode: writes to the `resources` table; row survives reload.
 * Mock / API mode: returns the resource unchanged so the caller can keep
 * its in-memory `pendingResources` list working as before.
 *
 * Wired from AddResourceDialog so newly-created resources persist when
 * the demo's `VITE_DATA_SOURCE=supabase`.
 */
export async function createResource(resource: Resource): Promise<Resource> {
  if (isSupabaseMode()) return sb.createResource(resource)
  // Mock + REST-API modes both fall back to "echo it back, caller keeps
  // an in-memory list" â€" fetchApi today only does GET-with-params, and
  // the REST-API mode for resources isn't fleshed out yet.
  return resource
}

export async function getResourceDemand(portfolioId?: string) {
  if (isSapMode()) return sapBackend.getResourceDemand(portfolioId)
  if (isSupabaseMode()) return sb.getResourceDemand(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/resources/demand', { portfolioId })
  const d = await loadMock<{ resourceDemand: Array<Record<string, unknown>> }>('resources.json')
  const all = d.resourceDemand ?? []
  return portfolioId ? all.filter((r: Record<string, unknown>) => r.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Risks
// ---------------------------------------------------------------------------
export async function getRisks(projectId?: string): Promise<Risk[]> {
  if (isSapMode()) return sapBackend.getRisks(projectId)
  if (isSupabaseMode()) return sb.getRisks(projectId)
  if (!isMockMode()) return fetchApi<Risk[]>('/risks', { projectId })
  const d = await loadMock<{ risks: Risk[] }>('risks.json')
  const all = d.risks ?? []
  return projectId ? all.filter(r => r.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Financial
// ---------------------------------------------------------------------------
export async function getBudgetData(projectId?: string) {
  if (isSapMode()) return sapBackend.getBudgetData(projectId)
  if (isSupabaseMode()) return sb.getBudgetData(projectId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/data', { projectId })
  const d = await loadMock<{ budgetData: Array<Record<string, unknown>> }>('financial/budget-data.json')
  const all = d.budgetData ?? []
  return projectId ? all.filter((b: Record<string, unknown>) => b.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Milestones
// ---------------------------------------------------------------------------
export async function getMilestones(projectId?: string): Promise<Milestone[]> {
  if (isSapMode()) return sapBackend.getMilestones(projectId)
  if (isSupabaseMode()) return sb.getMilestones(projectId)
  if (!isMockMode()) return fetchApi<Milestone[]>('/milestones', { projectId })
  const d = await loadMock<{ milestones: Milestone[] }>('milestones.json')
  const all = d.milestones ?? []
  return projectId ? all.filter((m) => m.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------
export async function getIssues(projectId?: string) {
  if (isSapMode()) return sapBackend.getIssues(projectId)
  if (isSupabaseMode()) return sb.getIssues(projectId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/issues', { projectId })
  const d = await loadMock<{ issues: Array<Record<string, unknown>> }>('issues.json')
  const all = d.issues ?? []
  return projectId ? all.filter((i: Record<string, unknown>) => i.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Resource Assignments
// ---------------------------------------------------------------------------
export async function getResourceAssignments(projectId?: string) {
  if (isSapMode()) return sapBackend.getResourceAssignments(projectId)
  if (isSupabaseMode()) return sb.getResourceAssignments(projectId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/resource-assignments', { projectId })
  const d = await loadMock<{ assignments: Array<Record<string, unknown>> }>('resource-assignments.json')
  const all = d.assignments ?? []
  return projectId ? all.filter((a: Record<string, unknown>) => a.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------
export async function getBudgetReleases(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetReleases(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/releases', { portfolioId })
  const d = await loadMock<{ budgetReleases: Array<Record<string, unknown>> }>('portfolio/budget-releases.json')
  const all = d.budgetReleases ?? []
  return portfolioId ? all.filter((r: Record<string, unknown>) => r.portfolioId === portfolioId) : all
}

export async function getBudgetCategories() {
  if (isSupabaseMode()) return sb.getBudgetCategories()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/categories')
  const d = await loadMock<{ budgetCategories: Array<Record<string, unknown>> }>('portfolio/budget-categories.json')
  return d.budgetCategories ?? []
}

export async function getBudgetAllocations(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetAllocations(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/allocations', { fromPortfolioId: portfolioId })
  const d = await loadMock<{ budgetAllocations: Array<Record<string, unknown>> }>('portfolio/budget-allocations.json')
  const all = d.budgetAllocations ?? []
  return portfolioId ? all.filter((a: Record<string, unknown>) => a.fromPortfolioId === portfolioId) : all
}

export async function getBudgetTransfers(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetTransfers(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/transfers', { portfolioId })
  const d = await loadMock<{ budgetTransfers: Array<Record<string, unknown>> }>('portfolio/budget-transfers.json')
  const all = d.budgetTransfers ?? []
  return portfolioId ? all.filter((t: Record<string, unknown>) => t.portfolioId === portfolioId) : all
}

// In-memory store for budget change requests created from other pages (e.g., scenario planning)
const pendingBudgetChangeRequests: Array<Record<string, unknown>> = []

export function addBudgetChangeRequest(bcr: Record<string, unknown>) {
  pendingBudgetChangeRequests.push(bcr)
}

export async function getBudgetChangeRequests(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetChangeRequests(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/change-requests', { portfolioId })
  const d = await loadMock<{ budgetChangeRequests: Array<Record<string, unknown>> }>('portfolio/budget-change-requests.json')
  const all = [...(d.budgetChangeRequests ?? []), ...pendingBudgetChangeRequests]
  return portfolioId ? all.filter((c: Record<string, unknown>) => c.portfolioId === portfolioId) : all
}

export async function getBudgetLedger(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetLedger(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/ledger', { portfolioId })
  const d = await loadMock<{ budgetLedger: Array<Record<string, unknown>> }>('portfolio/budget-ledger.json')
  const all = d.budgetLedger ?? []
  return portfolioId ? all.filter((l: Record<string, unknown>) => l.portfolioId === portfolioId) : all
}

export async function getBudgetFundingSources(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetFundingSources(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/funding-sources', { portfolioId })
  const d = await loadMock<{ budgetFundingSources: Array<Record<string, unknown>> }>('portfolio/budget-funding.json')
  const all = d.budgetFundingSources ?? []
  return portfolioId ? all.filter((s: Record<string, unknown>) => s.portfolioId === portfolioId) : all
}

export async function getBudgetFundingRequests(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getBudgetFundingRequests(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/budget/funding-requests', { portfolioId })
  const d = await loadMock<{ budgetFundingRequests: Array<Record<string, unknown>> }>('portfolio/budget-funding.json')
  const all = d.budgetFundingRequests ?? []
  return portfolioId ? all.filter((r: Record<string, unknown>) => r.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Project Requests
// ---------------------------------------------------------------------------
export async function getProjectRequests(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getProjectRequests(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/requests', { portfolioId })
  const d = await loadMock<{ projectRequests: Array<Record<string, unknown>> }>('portfolio/project-requests.json')
  const all = d.projectRequests ?? []
  return portfolioId ? all.filter((r: Record<string, unknown>) => r.portfolioId === portfolioId) : all
}

export async function createProjectRequest(request: Record<string, unknown>) {
  if (isSupabaseMode()) return sb.createProjectRequest(request)
  if (!isMockMode()) {
    return fetchApi<Record<string, unknown>>('/portfolio/requests', undefined)
  }
  // In mock mode, add to the in-memory cache so it shows up immediately
  const d = await loadMock<{ projectRequests: Array<Record<string, unknown>> }>('portfolio/project-requests.json')
  const all = d.projectRequests ?? []
  all.unshift(request)
  return request
}

export async function updateProjectRequestStatus(
  requestId: string,
  status: string,
  review?: { reviewedByName?: string; convertedToProjectId?: string },
) {
  if (isSupabaseMode()) return sb.updateProjectRequestStatus(requestId, status, review)
  if (!isMockMode()) return fetchApi<Record<string, unknown>>(`/portfolio/requests/${requestId}`, undefined)
  // Mock mode: mutate the in-memory cache so the change shows immediately.
  const d = await loadMock<{ projectRequests: Array<Record<string, unknown>> }>('portfolio/project-requests.json')
  const row = (d.projectRequests ?? []).find((r) => r.id === requestId)
  if (row) {
    row.status = status
    row.reviewedDate = new Date().toISOString()
    if (review?.reviewedByName) row.reviewedByName = review.reviewedByName
    if (review?.convertedToProjectId) row.convertedToProjectId = review.convertedToProjectId
  }
  return { id: requestId, status }
}

/**
 * #124 — Materialize an approved project request into a real Draft project.
 * Reuses createDraftProject (typed `projects` insert + defaults; RLS open via
 * 00263), maps the request's fields onto the new project, then links the
 * request back to it (data.convertedToProjectId). Returns the new project so
 * the caller can navigate to it. The project starts in Draft, where the
 * startup checklist takes over — exactly the intake→project flow the
 * "From request to project" journey describes.
 */
/** Map any request funding value to a `projects.funding_type` CHECK value (00061). */
function normalizeProjectFundingType(v: unknown): string {
  const s = String(v ?? '').trim().toLowerCase()
  if (s.includes('external')) return 'External'
  if (s.includes('irad')) return 'IRAD'
  if (s.includes('crad')) return 'CRAD'
  if (s.includes('production')) return 'Production'
  if (s.includes('other')) return 'Other'
  return 'Internal' // covers 'internal', empty, and anything unrecognized
}

export async function convertProjectRequestToProject(request: Record<string, unknown>): Promise<Project> {
  const budget = Number(request.estimatedBudget) || 0
  const rawCode = typeof request.code === 'string' ? request.code : ''
  const code = rawCode ? rawCode.replace(/^REQ[-_]?/i, 'PRJ-') : undefined
  // projects.funding_type has a CHECK constraint (00061): only these exact,
  // case-sensitive values are allowed. Normalize the request's value (which
  // can be a phrase like "IRAD-funded", a legacy code, or lowercase) so the
  // insert never trips the constraint.
  const fundingType = normalizeProjectFundingType(request.fundingType)
  const project = await createDraftProject({
    name: (request.name as string) || 'Converted Project',
    description: (request.description as string) ?? '',
    portfolioId: (request.portfolioId as string) ?? '',
    projectType: (request.projectType as string) ?? '',
    fundingType: fundingType as Project['fundingType'],
    priority: (request.priority as Project['priority']) ?? 'Medium',
    businessOwner: (request.sponsorName as string) || (request.requestedByName as string) || '',
    projectManagerName: (request.sponsorName as string) || '',
    startDate: (request.requestedStartDate as string) || undefined,
    budget: { total: budget, spent: 0, committed: 0, available: budget, currency: 'USD' },
    ...(code ? { code } : {}),
  })
  // Link the request to its new project (status stays Approved).
  await updateProjectRequestStatus(
    request.id as string,
    (request.status as string) || 'Approved',
    { convertedToProjectId: project.id },
  )
  return project
}

export async function getProjectRequestById(requestId: string) {
  if (isSupabaseMode()) return sb.getProjectRequestById(requestId)
  if (!isMockMode()) {
    try { return await fetchApi<Record<string, unknown>>(`/portfolio/requests/${requestId}`) }
    catch { return null }
  }
  const d = await loadMock<{ projectRequests: Array<Record<string, unknown>> }>('portfolio/project-requests.json')
  const all = d.projectRequests ?? []
  return all.find((r: Record<string, unknown>) => r.id === requestId) ?? null
}

// ---------------------------------------------------------------------------
// Strategic Priorities
// ---------------------------------------------------------------------------
export async function getStrategicPriorities(portfolioId?: string) {
  if (isSapMode()) return sapBackend.getStrategicPriorities(portfolioId)
  if (isSupabaseMode()) return sb.getStrategicPriorities(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/strategic-priorities', { portfolioId })
  const d = await loadMock<{ strategicPriorities: Array<Record<string, unknown>> }>('portfolio/strategic-priorities.json')
  const all = d.strategicPriorities ?? []
  return portfolioId ? all.filter((p: Record<string, unknown>) => p.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Lessons Learned (gap-list #15)
// ---------------------------------------------------------------------------
export async function getLessonsLearned(projectId?: string): Promise<LessonLearned[]> {
  if (isSupabaseMode()) return sb.getLessonsLearned(projectId) as unknown as Promise<LessonLearned[]>
  if (!isMockMode()) return fetchApi<LessonLearned[]>('/lessons-learned', { projectId })
  // Mock mode: no JSON seed file ships with the repo, return [].
  return []
}

export async function createLessonLearned(payload: LessonLearned): Promise<LessonLearned> {
  if (isSupabaseMode()) return sb.createLessonLearned(payload) as unknown as Promise<LessonLearned>
  // Mock + REST: echo the payload so optimistic UI works.
  return payload
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------
export async function getScenarios(portfolioId?: string) {
  if (isSapMode()) return sapBackend.getScenarios(portfolioId)
  if (isSupabaseMode()) return sb.getScenarios(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/scenarios', { portfolioId })
  const d = await loadMock<{ scenarios: Array<Record<string, unknown>> }>('portfolio/scenarios.json')
  const all = d.scenarios ?? []
  return portfolioId ? all.filter((s: Record<string, unknown>) => s.portfolioId === portfolioId) : all
}

export async function getScenarioInitiatives(scenarioId?: string) {
  if (isSupabaseMode()) return sb.getScenarioInitiatives(scenarioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/scenarios/initiatives', { scenarioId })
  const d = await loadMock<{ scenarioInitiatives: Array<Record<string, unknown>> }>('portfolio/scenario-initiatives.json')
  const all = d.scenarioInitiatives ?? []
  return scenarioId ? all.filter((i: Record<string, unknown>) => i.scenarioId === scenarioId) : all
}

export async function getScenarioConstraints(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getScenarioConstraints(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/scenarios/constraints', { portfolioId })
  const d = await loadMock<{ scenarioConstraints: Array<Record<string, unknown>> }>('portfolio/scenario-constraints.json')
  const all = d.scenarioConstraints ?? []
  return portfolioId ? all.filter((c: Record<string, unknown>) => c.portfolioId === portfolioId) : all
}

export async function getScenarioChanges(scenarioId?: string) {
  if (isSupabaseMode()) return sb.getScenarioChanges(scenarioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/scenarios/changes', { scenarioId })
  const d = await loadMock<{ scenarioChanges: Array<Record<string, unknown>> }>('portfolio/scenario-changes.json')
  const all = d.scenarioChanges ?? []
  return scenarioId ? all.filter((c: Record<string, unknown>) => c.scenarioId === scenarioId) : all
}

export async function getScenarioTemplates() {
  if (isSupabaseMode()) return sb.getScenarioTemplates()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/scenarios/templates')
  const d = await loadMock<{ scenarioTemplates: Array<Record<string, unknown>> }>('portfolio/scenario-templates.json')
  return d.scenarioTemplates ?? []
}

// ---------------------------------------------------------------------------
// Capacity & Demand
// ---------------------------------------------------------------------------
export async function getCapacitySupply(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getCapacitySupply(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/capacity/supply', { portfolioId })
  const d = await loadMock<{ capacitySupply: Array<Record<string, unknown>> }>('portfolio/capacity-supply.json')
  const all = d.capacitySupply ?? []
  return portfolioId ? all.filter((c: Record<string, unknown>) => c.portfolioId === portfolioId) : all
}

export async function getInitiativeDemands(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getInitiativeDemands(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/capacity/initiative-demands', { portfolioId })
  const d = await loadMock<{ initiativeDemands: Array<Record<string, unknown>> }>('portfolio/initiative-demands.json')
  const all = d.initiativeDemands ?? []
  return portfolioId ? all.filter((i: Record<string, unknown>) => i.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Portfolio Workflows
// ---------------------------------------------------------------------------
export async function getPortfolioWorkflows(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getPortfolioWorkflows(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/workflows', { portfolioId })
  const d = await loadMock<{ portfolioWorkflows: Array<Record<string, unknown>> }>('portfolio/portfolio-workflows.json')
  const all = d.portfolioWorkflows ?? []
  return portfolioId ? all.filter((w: Record<string, unknown>) => w.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Portfolio Attributes
// ---------------------------------------------------------------------------
export async function getPortfolioAttributes(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getPortfolioAttributes(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/attributes', { portfolioId })
  const d = await loadMock<{ portfolioAttributes: Array<Record<string, unknown>> }>('portfolio/portfolio-attributes.json')
  const all = d.portfolioAttributes ?? []
  return portfolioId ? all.filter((a: Record<string, unknown>) => a.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Portfolio Assignments
// ---------------------------------------------------------------------------
export async function getPortfolioAssignments(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getPortfolioAssignments(portfolioId)
  if (!isMockMode()) return fetchApi<{ people: Array<Record<string, unknown>>; organizations: Array<Record<string, unknown>> }>('/portfolio/assignments', { portfolioId })
  const d = await loadMock<{ portfolioAssignments: { people: Array<Record<string, unknown>>; organizations: Array<Record<string, unknown>> } }>('portfolio/portfolio-assignments.json')
  const all = d.portfolioAssignments ?? { people: [], organizations: [] }
  if (!portfolioId) return all
  return {
    people: (all.people ?? []).filter((p: Record<string, unknown>) => p.portfolioId === portfolioId),
    organizations: (all.organizations ?? []).filter((o: Record<string, unknown>) => o.portfolioId === portfolioId),
  }
}

// ---------------------------------------------------------------------------
// Attribute Rules
// ---------------------------------------------------------------------------
export async function getAttributeValidationRules(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getAttributeValidationRules(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/attribute-rules/validation', { portfolioId })
  const d = await loadMock<{ validationRules: Array<Record<string, unknown>> }>('portfolio/attribute-rules.json')
  const all = d.validationRules ?? []
  return portfolioId ? all.filter((r: Record<string, unknown>) => r.portfolioId === portfolioId) : all
}

export async function getAttributeDeterminationRules(portfolioId?: string) {
  if (isSupabaseMode()) return sb.getAttributeDeterminationRules(portfolioId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/attribute-rules/determination', { portfolioId })
  const d = await loadMock<{ determinationRules: Array<Record<string, unknown>> }>('portfolio/attribute-rules.json')
  const all = d.determinationRules ?? []
  return portfolioId ? all.filter((r: Record<string, unknown>) => r.portfolioId === portfolioId) : all
}

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------
export async function getOrganizations() {
  if (isSapMode()) return sapBackend.getOrganizations()
  if (isSupabaseMode()) return sb.getOrganizations()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/portfolio/organizations')
  const d = await loadMock<{ organizations: Array<Record<string, unknown>> }>('portfolio/organizations.json')
  return d.organizations ?? []
}

/** Organizations flagged as production (data.dataClass === 'production').
 *  These are the only orgs a production project may be aligned to. */
export async function getProductionOrganizations(): Promise<Array<Record<string, unknown>>> {
  const orgs = await getOrganizations()
  return (orgs as Array<Record<string, unknown>>).filter((o) => o.dataClass === 'production')
}

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------
export async function getMasterContracts() {
  if (isSapMode()) return sapBackend.getMasterContracts()
  if (isSupabaseMode()) {
    const rows = await sb.getMasterContracts()
    return isSandboxMode() ? rows.filter(r => !isDemoSeedId(String((r as { id?: unknown }).id ?? ''))) : rows
  }
  if (!isMockMode()) {
    const rows = await fetchApi<Array<Record<string, unknown>>>('/contracts')
    return isSandboxMode() ? rows.filter(r => !isDemoSeedId(String(r.id ?? ''))) : rows
  }
  const d = await loadMock<{ contracts: Array<Record<string, unknown>> }>('contracts/master-contracts.json')
  return d.contracts ?? []
}

export async function getTaskOrders(contractId?: string) {
  if (isSupabaseMode()) return sb.getTaskOrders(contractId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/contracts/task-orders', { masterContractId: contractId })
  const d = await loadMock<{ taskOrders: Array<Record<string, unknown>> }>('contracts/task-orders.json')
  const all = d.taskOrders ?? []
  return contractId ? all.filter((t: Record<string, unknown>) => t.masterContractId === contractId) : all
}

export async function getSalesOrders(taskOrderId?: string) {
  if (isSupabaseMode()) return sb.getSalesOrders(taskOrderId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/contracts/sales-orders', { taskOrderId })
  const d = await loadMock<{ salesOrders: Array<Record<string, unknown>> }>('contracts/sales-orders.json')
  const all = d.salesOrders ?? []
  return taskOrderId ? all.filter((s: Record<string, unknown>) => s.taskOrderId === taskOrderId) : all
}

export async function getContractLineItems(salesOrderId?: string) {
  if (isSupabaseMode()) return sb.getContractLineItems(salesOrderId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/contracts/line-items', { salesOrderId })
  const d = await loadMock<{ contractLineItems: Array<Record<string, unknown>> }>('contracts/contract-line-items.json')
  const all = d.contractLineItems ?? []
  return salesOrderId ? all.filter((c: Record<string, unknown>) => c.salesOrderId === salesOrderId) : all
}

// ---------------------------------------------------------------------------
// EVM
// ---------------------------------------------------------------------------
export async function getEvmBaselines(projectId?: string): Promise<EvmBaselineSnapshot[]> {
  if (isSupabaseMode()) return sb.getEvmBaselines(projectId) as unknown as Promise<EvmBaselineSnapshot[]>
  if (!isMockMode()) return fetchApi<EvmBaselineSnapshot[]>('/evm/baselines', { projectId })
  const d = await loadMock<{ baselines: EvmBaselineSnapshot[] }>('evm/baselines.json')
  const all = d.baselines ?? []
  return projectId ? all.filter((b) => b.projectId === projectId) : all
}

export async function getEvmTimeSeries(projectId?: string): Promise<EvmProjectTimeSeries[]> {
  if (isSupabaseMode()) return sb.getEvmTimeSeries(projectId) as unknown as Promise<EvmProjectTimeSeries[]>
  if (!isMockMode()) return fetchApi<EvmProjectTimeSeries[]>('/evm/time-series', { projectId })
  const d = await loadMock<{ evmData: EvmProjectTimeSeries[] }>('evm/earned-value-data.json')
  const all = d.evmData ?? []
  return projectId ? all.filter((e) => e.projectId === projectId) : all
}

export async function getPeriodicActuals(projectId?: string): Promise<EvmPeriodicActual[]> {
  if (isSapMode()) return sapBackend.getPeriodicActuals(projectId)
  if (isSupabaseMode()) return sb.getPeriodicActuals(projectId) as unknown as Promise<EvmPeriodicActual[]>
  if (!isMockMode()) return fetchApi<EvmPeriodicActual[]>('/evm/periodic-actuals', { projectId })
  const d = await loadMock<{ actuals: EvmPeriodicActual[] }>('evm/periodic-actuals.json')
  const all = d.actuals ?? []
  return projectId ? all.filter((a) => a.projectId === projectId) : all
}

export async function getEvtAssignments(projectId?: string): Promise<EvtAssignment[]> {
  if (isSupabaseMode()) return sb.getEvtAssignments(projectId) as unknown as Promise<EvtAssignment[]>
  if (!isMockMode()) return fetchApi<EvtAssignment[]>('/evm/evt-assignments', { projectId })
  const d = await loadMock<{ evtAssignments: EvtAssignment[] }>('evm/evt-assignments.json')
  const all = d.evtAssignments ?? []
  return projectId ? all.filter((a) => a.projectId === projectId) : all
}

export async function upsertEvtAssignment(
  payload: Pick<EvtAssignment, 'id' | 'projectId' | 'wbsId' | 'wbsName' | 'nodeType' | 'technique' | 'baseWorkPackageId' | 'baseWorkPackageName' | 'apportionmentRatio' | 'qbdSourceType'>,
): Promise<EvtAssignment> {
  if (isSupabaseMode()) {
    return sb.upsertEvtAssignment(payload) as unknown as Promise<EvtAssignment>
  }
  // Mock + REST modes echo the input back so the UI optimistic-update path
  // stays consistent. Real persistence only fires in Supabase mode.
  return { milestones: null, ...payload } as EvtAssignment
}

export async function getProgressEntries(projectId?: string, period?: string): Promise<ProgressEntry[]> {
  if (isSupabaseMode()) return sb.getProgressEntries(projectId, period) as unknown as Promise<ProgressEntry[]>
  if (!isMockMode()) return fetchApi<ProgressEntry[]>('/evm/progress-entries', { projectId, period })
  const d = await loadMock<{ progressEntries: ProgressEntry[] }>('evm/progress-entries.json')
  let all = d.progressEntries ?? []
  if (projectId) all = all.filter((e) => e.projectId === projectId)
  if (period) all = all.filter((e) => e.period === period)
  return all
}

export async function upsertProgressEntry(payload: {
  id: string
  projectId: string
  wbsId: string
  wbsName: string
  period: string
  technique: string
  status?: string
  percentComplete?: number | null
  actualCost?: number
  actualHours?: number
  notes?: string | null
  isAutoCalculated?: boolean
  submittedBy?: string | null
}): Promise<ProgressEntry> {
  if (isSupabaseMode()) {
    return sb.upsertProgressEntry(payload) as unknown as Promise<ProgressEntry>
  }
  return {
    earnedValue: 0, milestonesCompleted: null, qbdBackupSummary: null,
    submittedDate: new Date().toISOString().slice(0, 10), locked: false,
    ...payload,
  } as unknown as ProgressEntry
}

export async function getEvmThresholdConfig(projectId: string): Promise<EvmThresholdConfig | null> {
  if (isSupabaseMode()) return sb.getEvmThresholdConfig(projectId) as Promise<EvmThresholdConfig | null>
  if (!isMockMode()) return fetchApi<EvmThresholdConfig | null>('/evm/threshold-config', { projectId })
  const d = await loadMock<{ thresholdConfigs: EvmThresholdConfig[] }>('evm/threshold-configs.json')
  return (d.thresholdConfigs ?? []).find((c) => c.projectId === projectId) ?? null
}

export async function upsertEvmThresholdConfig(
  projectId: string,
  config: Omit<EvmThresholdConfig, 'id' | 'projectId'>,
): Promise<EvmThresholdConfig> {
  if (isSupabaseMode()) {
    return sb.upsertEvmThresholdConfig(
      projectId,
      config as unknown as Record<string, number | null>,
    ) as unknown as Promise<EvmThresholdConfig>
  }
  // Mock + REST modes echo the input back as a synthetic record so the UI
  // optimistic-update path stays consistent. The actual write is a no-op.
  return { id: `tc_${projectId}`, projectId, ...config } as EvmThresholdConfig
}

export async function getVarianceNarratives(projectId?: string): Promise<VarianceNarrative[]> {
  if (isSupabaseMode()) return sb.getVarianceNarratives(projectId) as unknown as Promise<VarianceNarrative[]>
  if (!isMockMode()) return fetchApi<VarianceNarrative[]>('/evm/variance-narratives', { projectId })
  const d = await loadMock<{ varianceNarratives: VarianceNarrative[] }>('evm/variance-narratives.json')
  const all = d.varianceNarratives ?? []
  return projectId ? all.filter((n) => n.projectId === projectId) : all
}

export async function createVarianceNarrative(payload: {
  projectId: string
  wbsId: string
  wbsName: string
  period: string
  triggerType: 'SPI' | 'CPI' | 'CV_Dollar' | 'SV_Dollar' | 'EAC_Overrun' | 'TCPI'
  triggerValue: number
  thresholdValue: number
  rootCause: string
  impactAssessment?: string | null
  correctiveAction: string
  expectedRecoveryDate?: string | null
  responsibleParty: string
  status?: 'Draft' | 'Submitted' | 'Reviewed' | 'Closed'
  createdBy?: string | null
}): Promise<VarianceNarrative> {
  if (isSupabaseMode()) {
    return sb.createVarianceNarrative(payload) as unknown as Promise<VarianceNarrative>
  }
  // Mock + REST: synthesize an id so the UI can display the new row immediately.
  const today = new Date().toISOString().slice(0, 10)
  return {
    id: `vn_${Date.now()}`,
    impactAssessment: payload.impactAssessment ?? '',
    expectedRecoveryDate: payload.expectedRecoveryDate ?? null,
    status: payload.status ?? 'Draft',
    createdBy: payload.createdBy ?? '',
    createdDate: today,
    updatedDate: today,
    ...payload,
  } as VarianceNarrative
}

// ---------------------------------------------------------------------------
// Revenue Recognition
// ---------------------------------------------------------------------------
export async function getRevenueRecognition(projectId?: string) {
  if (isSupabaseMode()) return sb.getRevenueRecognition(projectId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/financial/revenue-recognition', { projectId })
  const d = await loadMock<{ revenueRecognition: Array<Record<string, unknown>> }>('financial/revenue-recognition.json')
  const all = d.revenueRecognition ?? []
  return projectId ? all.filter((r: Record<string, unknown>) => r.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Financial Actuals
// ---------------------------------------------------------------------------
export async function getFinancialActuals(projectId?: string) {
  if (isSapMode()) return sapBackend.getFinancialActuals(projectId)
  if (isSupabaseMode()) return sb.getFinancialActuals(projectId)
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/financial/actuals', { projectId })
  const d = await loadMock<{ actuals: Array<Record<string, unknown>> }>('financial/actuals.json')
  const all = d.actuals ?? []
  return projectId ? all.filter((a: Record<string, unknown>) => a.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------
export async function getWorkflowDefinitions() {
  if (isSupabaseMode()) return sb.getWorkflowDefinitions()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/workflows/definitions')
  const d = await loadMock<{ definitions: Array<Record<string, unknown>> }>('workflow/workflow-definitions-v2.json')
  return d.definitions ?? []
}

export async function getWorkflowInstances() {
  if (isSupabaseMode()) return sb.getWorkflowInstances()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/workflows/instances')
  const d = await loadMock<{ instances: Array<Record<string, unknown>> }>('workflow/workflow-instances-v2.json')
  return d.instances ?? []
}

export async function getWorkflowAuditLog() {
  if (isSupabaseMode()) return sb.getWorkflowAuditLog()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/workflows/audit-log')
  const d = await loadMock<{ auditLog: Array<Record<string, unknown>> }>('workflow/workflow-audit-log.json')
  return d.auditLog ?? []
}

export async function getWorkflowArtifacts() {
  if (isSupabaseMode()) return sb.getWorkflowArtifacts()
  if (!isMockMode()) return fetchApi<{ artifacts: Array<Record<string, unknown>>; requirements: Array<Record<string, unknown>> }>('/workflows/artifacts')
  const d = await loadMock<{ submittedArtifacts: Array<Record<string, unknown>>; artifactRequirements: Array<Record<string, unknown>> }>('workflow/workflow-artifacts.json')
  return { artifacts: d.submittedArtifacts ?? [], requirements: d.artifactRequirements ?? [] }
}

export async function getWorkflowSlaConfig() {
  if (isSupabaseMode()) return sb.getWorkflowSlaConfig()
  if (!isMockMode()) return fetchApi<Array<Record<string, unknown>>>('/workflows/sla-config')
  const d = await loadMock<{ slaRules: Array<Record<string, unknown>> }>('workflow/workflow-sla-config.json')
  return d.slaRules ?? []
}

// ---------------------------------------------------------------------------
// Workflows v3 (AI-driven model: workflow + policy_set + run + plan + event)
// ---------------------------------------------------------------------------
import type { Workflow, PolicySet, WorkflowRun, WorkflowPlan, WorkflowEvent, WorkflowPlanStep, PlanSource } from '@/types/workflows-v3'
import type { WorkflowDraft, WorkflowSpec, WorkflowChatHistory, WorkflowDraftStatus } from '@/types/workflow-spec'
import type { FormDraft, PublishedForm, FormSubmission } from '@/types/form-spec'
import type { PeopleGroup } from '@/types/people-group'

export async function getWorkflowsV3(): Promise<Workflow[]> {
  if (isSupabaseMode()) return sb.getWorkflowsV3()
  if (!isMockMode()) return fetchApi<Workflow[]>('/workflows-v3')
  const d = await loadMock<{ workflows: Workflow[] }>('workflow-v3/workflows.json')
  return d.workflows ?? []
}

export async function getWorkflowV3(id: string): Promise<Workflow | null> {
  const all = await getWorkflowsV3()
  return all.find(w => w.id === id) ?? null
}

export async function getPolicySets(portfolioId?: string): Promise<PolicySet[]> {
  if (isSupabaseMode()) return sb.getPolicySets(portfolioId)
  if (!isMockMode()) return fetchApi<PolicySet[]>('/policy-sets', { portfolioId })
  const d = await loadMock<{ policySets: PolicySet[] }>('workflow-v3/policy-sets.json')
  const all = d.policySets ?? []
  return portfolioId ? all.filter(p => p.portfolioId === portfolioId) : all
}

export async function getPolicySetForPortfolio(portfolioId: string): Promise<PolicySet | null> {
  const sets = await getPolicySets(portfolioId)
  // Pick the latest effective version
  const active = sets
    .filter(s => !s.effectiveTo || new Date(s.effectiveTo) >= new Date())
    .sort((a, b) => b.version - a.version)
  return active[0] ?? null
}

export async function getWorkflowRuns(opts?: { portfolioId?: string; workflowId?: string; status?: string; contextType?: string; contextId?: string }): Promise<WorkflowRun[]> {
  if (isSupabaseMode()) return sb.getWorkflowRuns(opts)
  if (!isMockMode()) return fetchApi<WorkflowRun[]>('/workflow-runs', opts as Record<string, string | undefined>)
  const d = await loadMock<{ runs: WorkflowRun[] }>('workflow-v3/workflow-runs.json')
  let all = d.runs ?? []
  if (opts?.portfolioId) all = all.filter(r => r.portfolioId === opts.portfolioId)
  if (opts?.workflowId) all = all.filter(r => r.workflowId === opts.workflowId)
  if (opts?.status) all = all.filter(r => r.status === opts.status)
  if (opts?.contextType) all = all.filter(r => r.contextType === opts.contextType)
  if (opts?.contextId) all = all.filter(r => r.contextId === opts.contextId)
  return all
}

export async function getWorkflowRun(id: string): Promise<WorkflowRun | null> {
  const all = await getWorkflowRuns()
  return all.find(r => r.id === id) ?? null
}

export async function getWorkflowPlans(runId?: string): Promise<WorkflowPlan[]> {
  if (isSupabaseMode()) return sb.getWorkflowPlans(runId)
  if (!isMockMode()) return fetchApi<WorkflowPlan[]>('/workflow-plans', { runId })
  const d = await loadMock<{ plans: WorkflowPlan[] }>('workflow-v3/workflow-plans.json')
  const all = d.plans ?? []
  return runId ? all.filter(p => p.runId === runId) : all
}

export async function getLatestPlanForRun(runId: string): Promise<WorkflowPlan | null> {
  const plans = await getWorkflowPlans(runId)
  return plans.sort((a, b) => b.version - a.version)[0] ?? null
}

export async function getWorkflowEvents(runId?: string): Promise<WorkflowEvent[]> {
  if (isSupabaseMode()) return sb.getWorkflowEvents(runId)
  if (!isMockMode()) return fetchApi<WorkflowEvent[]>('/workflow-events', { runId })
  const d = await loadMock<{ events: WorkflowEvent[] }>('workflow-v3/workflow-events.json')
  const all = d.events ?? []
  return runId ? all.filter(e => e.runId === runId) : all
}

/**
 * Create a new draft plan for a run. Mock/api modes are no-ops that return
 * the would-be record. Supabase mode writes to workflow_plans + appends a
 * plan.drafted event.
 */
export async function createWorkflowPlan(input: {
  runId: string
  source: PlanSource
  plannerModel?: string | null
  rationale: string
  steps: WorkflowPlanStep[]
}): Promise<WorkflowPlan> {
  if (isSupabaseMode()) return sb.createWorkflowPlan(input)
  // Mock / api: return an in-memory draft
  return {
    id: `plan_${Date.now()}`,
    runId: input.runId,
    version: 1,
    source: input.source,
    plannerModel: input.plannerModel ?? null,
    rationale: input.rationale,
    status: 'draft',
    steps: input.steps,
    approvedBy: null,
    approvedAt: null,
    createdAt: new Date().toISOString(),
  }
}

export async function approveWorkflowPlan(planId: string, approver: string): Promise<void> {
  if (isSupabaseMode()) return sb.approveWorkflowPlan(planId, approver)
  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Workflow Builder (Studio) - Supabase-only feature (no mock/api shims).
// ---------------------------------------------------------------------------
function requireSupabaseForWorkflows(): void {
  if (!isSupabaseMode()) {
    throw new Error('The Workflow Builder requires the Supabase data source (set VITE_DATA_SOURCE=supabase).')
  }
}

export async function getWorkflowDrafts(opts?: { portfolioId?: string; status?: string }): Promise<WorkflowDraft[]> {
  requireSupabaseForWorkflows()
  return sb.getWorkflowDrafts(opts)
}
export async function getWorkflowDraft(id: string): Promise<WorkflowDraft | null> {
  requireSupabaseForWorkflows()
  return sb.getWorkflowDraft(id)
}
export async function createWorkflowDraft(input: {
  name?: string; portfolioId?: string | null; ownerId?: string | null; spec?: WorkflowSpec; aiGenerated?: boolean
}): Promise<WorkflowDraft> {
  requireSupabaseForWorkflows()
  return sb.createWorkflowDraft(input)
}
export async function updateWorkflowDraft(id: string, patch: {
  name?: string; status?: WorkflowDraftStatus; spec?: WorkflowSpec; chatHistory?: WorkflowChatHistory; promotedWorkflowId?: string | null
}): Promise<WorkflowDraft> {
  requireSupabaseForWorkflows()
  return sb.updateWorkflowDraft(id, patch)
}
export async function deleteWorkflowDraft(id: string): Promise<void> {
  requireSupabaseForWorkflows()
  return sb.deleteWorkflowDraft(id)
}

export async function createWorkflowV3(input: Parameters<typeof sb.createWorkflowV3>[0]): Promise<Workflow> {
  requireSupabaseForWorkflows()
  return sb.createWorkflowV3(input)
}
export async function updateWorkflowV3(id: string, patch: Parameters<typeof sb.updateWorkflowV3>[1]): Promise<Workflow> {
  requireSupabaseForWorkflows()
  return sb.updateWorkflowV3(id, patch)
}
export async function createWorkflowRun(input: Parameters<typeof sb.createWorkflowRun>[0]): Promise<WorkflowRun> {
  requireSupabaseForWorkflows()
  return sb.createWorkflowRun(input)
}
export async function updateWorkflowRun(id: string, patch: Parameters<typeof sb.updateWorkflowRun>[1]): Promise<void> {
  requireSupabaseForWorkflows()
  return sb.updateWorkflowRun(id, patch)
}
export async function updateWorkflowPlan(id: string, patch: Parameters<typeof sb.updateWorkflowPlan>[1], expectedUpdatedAt?: string): Promise<string | undefined> {
  requireSupabaseForWorkflows()
  return sb.updateWorkflowPlan(id, patch, expectedUpdatedAt)
}
export async function appendWorkflowEvent(input: Parameters<typeof sb.appendWorkflowEvent>[0]): Promise<WorkflowEvent> {
  requireSupabaseForWorkflows()
  return sb.appendWorkflowEvent(input)
}

// ---------------------------------------------------------------------------
// Forms Management (Forms Builder + submissions) - Supabase-only (no mock shims).
// ---------------------------------------------------------------------------
function requireSupabaseForForms(): void {
  if (!isSupabaseMode()) {
    throw new Error('Forms require the Supabase data source (set VITE_DATA_SOURCE=supabase).')
  }
}

export async function getFormDrafts(opts?: { portfolioId?: string; status?: string }): Promise<FormDraft[]> {
  requireSupabaseForForms()
  return sb.getFormDrafts(opts)
}
export async function getFormDraft(id: string): Promise<FormDraft | null> {
  requireSupabaseForForms()
  return sb.getFormDraft(id)
}
export async function createFormDraft(input: Parameters<typeof sb.createFormDraft>[0]): Promise<FormDraft> {
  requireSupabaseForForms()
  return sb.createFormDraft(input)
}
export async function updateFormDraft(id: string, patch: Parameters<typeof sb.updateFormDraft>[1]): Promise<FormDraft> {
  requireSupabaseForForms()
  return sb.updateFormDraft(id, patch)
}
export async function deleteFormDraft(id: string): Promise<void> {
  requireSupabaseForForms()
  return sb.deleteFormDraft(id)
}
export async function publishFormDraft(draftId: string): Promise<PublishedForm> {
  requireSupabaseForForms()
  return sb.publishFormDraft(draftId)
}
export async function getForms(opts?: { portfolioId?: string; allVersions?: boolean }): Promise<PublishedForm[]> {
  requireSupabaseForForms()
  return sb.getForms(opts)
}
export async function getForm(id: string): Promise<PublishedForm | null> {
  requireSupabaseForForms()
  return sb.getForm(id)
}
export async function getLatestFormVersion(id: string): Promise<PublishedForm | null> {
  requireSupabaseForForms()
  return sb.getLatestFormVersion(id)
}
export async function getFormBySlug(slug: string): Promise<PublishedForm | null> {
  requireSupabaseForForms()
  return sb.getFormBySlug(slug)
}
export async function setFormPublicShare(formId: string, isPublic: boolean): Promise<PublishedForm> {
  requireSupabaseForForms()
  return sb.setFormPublicShare(formId, isPublic)
}
export async function createFormSubmission(input: Parameters<typeof sb.createFormSubmission>[0]): Promise<FormSubmission> {
  requireSupabaseForForms()
  return sb.createFormSubmission(input)
}
export async function getFormSubmissions(opts: Parameters<typeof sb.getFormSubmissions>[0]): Promise<FormSubmission[]> {
  requireSupabaseForForms()
  return sb.getFormSubmissions(opts)
}
export async function updateFormSubmission(id: string, patch: Parameters<typeof sb.updateFormSubmission>[1]): Promise<FormSubmission> {
  requireSupabaseForForms()
  return sb.updateFormSubmission(id, patch)
}
export async function uploadFormAttachment(args: Parameters<typeof sb.uploadFormAttachment>[0]) {
  requireSupabaseForForms()
  return sb.uploadFormAttachment(args)
}
/** A short-lived signed link for opening a stored form attachment. */
export async function getFormAttachmentUrl(ref: Parameters<typeof sb.getFormAttachmentUrl>[0], expiresInSeconds?: number): Promise<string> {
  requireSupabaseForForms()
  return sb.getFormAttachmentUrl(ref, expiresInSeconds)
}
// Submission revision history (form_submission_revisions, migration 00509).
// Call these through services/forms/submissionRevisions.ts, which degrades when
// the table is not there yet.
export async function insertFormSubmissionRevision(input: Parameters<typeof sb.insertFormSubmissionRevision>[0]): Promise<void> {
  requireSupabaseForForms()
  return sb.insertFormSubmissionRevision(input)
}
export async function getFormSubmissionRevisions(submissionId: Parameters<typeof sb.getFormSubmissionRevisions>[0]): Promise<Awaited<ReturnType<typeof sb.getFormSubmissionRevisions>>> {
  requireSupabaseForForms()
  return sb.getFormSubmissionRevisions(submissionId)
}

// People Groups (usable as workflow assignees / approvers / recipients).
export async function getPeopleGroups(opts?: { portfolioId?: string }): Promise<PeopleGroup[]> {
  requireSupabaseForForms()
  return sb.getPeopleGroups(opts)
}
export async function createPeopleGroup(input: Parameters<typeof sb.createPeopleGroup>[0]): Promise<PeopleGroup> {
  requireSupabaseForForms()
  return sb.createPeopleGroup(input)
}
export async function updatePeopleGroup(id: string, patch: Parameters<typeof sb.updatePeopleGroup>[1]): Promise<PeopleGroup> {
  requireSupabaseForForms()
  return sb.updatePeopleGroup(id, patch)
}
export async function deletePeopleGroup(id: string): Promise<void> {
  requireSupabaseForForms()
  return sb.deletePeopleGroup(id)
}
// Shared select lists (form_option_lists, migration 00522). Read them through
// hooks/useOptionLists.ts, which caches one read per page load.
export async function getOptionLists(): Promise<Awaited<ReturnType<typeof sb.getOptionLists>>> {
  requireSupabaseForForms()
  return sb.getOptionLists()
}
export async function getOptionList(id: string): Promise<Awaited<ReturnType<typeof sb.getOptionList>>> {
  requireSupabaseForForms()
  return sb.getOptionList(id)
}
export async function createOptionList(input: Parameters<typeof sb.createOptionList>[0]): Promise<Awaited<ReturnType<typeof sb.createOptionList>>> {
  requireSupabaseForForms()
  return sb.createOptionList(input)
}
export async function updateOptionList(id: string, patch: Parameters<typeof sb.updateOptionList>[1]): Promise<Awaited<ReturnType<typeof sb.updateOptionList>>> {
  requireSupabaseForForms()
  return sb.updateOptionList(id, patch)
}
export async function setOptionListItemsActive(id: string, values: string[], active: boolean, actor?: string | null): Promise<Awaited<ReturnType<typeof sb.setOptionListItemsActive>>> {
  requireSupabaseForForms()
  return sb.setOptionListItemsActive(id, values, active, actor)
}

// Routing matrices (wff_routing_matrices, migration 00533). Read by Matrix
// review / Department review steps when they open; edited on Setup > Routing Matrix.
export async function getRoutingMatrices(): Promise<Awaited<ReturnType<typeof sb.getRoutingMatrices>>> {
  requireSupabaseForForms()
  return sb.getRoutingMatrices()
}
export async function getRoutingMatrix(id: string): Promise<Awaited<ReturnType<typeof sb.getRoutingMatrix>>> {
  requireSupabaseForForms()
  return sb.getRoutingMatrix(id)
}
export async function createRoutingMatrix(...args: Parameters<typeof sb.createRoutingMatrix>): Promise<Awaited<ReturnType<typeof sb.createRoutingMatrix>>> {
  requireSupabaseForForms()
  return sb.createRoutingMatrix(...args)
}
export async function updateRoutingMatrix(...args: Parameters<typeof sb.updateRoutingMatrix>): Promise<Awaited<ReturnType<typeof sb.updateRoutingMatrix>>> {
  requireSupabaseForForms()
  return sb.updateRoutingMatrix(...args)
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------
/** Active demo persona (PersonaSwitcher snapshot) for notification scoping. */
function getDemoPersonaForScoping(): { id: string; systemRole: string } | null {
  try {
    const raw = localStorage.getItem('ppm-demo-persona')
    if (!raw) return null
    const p = JSON.parse(raw) as Record<string, unknown>
    if (p && typeof p.id === 'string') {
      return { id: p.id, systemRole: typeof p.systemRole === 'string' ? p.systemRole : '' }
    }
  } catch { /* ignore disabled storage / bad json */ }
  return null
}

/**
 * Person-specific notification scoping - hybrid (per-user OR per-role), mirroring
 * real-world routing. A persona sees notifications addressed to them directly
 * (`assignedTo === id`) OR routed to their role (`assignedRole === systemRole`),
 * plus org-wide broadcasts (both null). With no active persona snapshot (non-demo
 * / real auth) the list is returned unscoped - RLS handles tenancy there.
 */
function scopeNotificationsToPersona<T extends Record<string, unknown>>(rows: T[]): T[] {
  const persona = getDemoPersonaForScoping()
  if (!persona) return rows
  return rows.filter((n) => {
    const to = (n.assignedTo as string | null | undefined) ?? null
    const role = (n.assignedRole as string | null | undefined) ?? null
    if (to == null && role == null) return true                                        // broadcast → everyone
    if (to != null && to === persona.id) return true                                   // addressed to me
    if (role != null && persona.systemRole !== '' && role === persona.systemRole) return true // my role
    return false
  })
}

export async function getNotifications() {
  let rows: Array<Record<string, unknown>>
  if (isSapMode()) rows = (await sapBackend.getNotifications()) as Array<Record<string, unknown>>
  else if (isSupabaseMode()) rows = (await sb.getNotifications()) as Array<Record<string, unknown>>
  else if (!isMockMode()) rows = await fetchApi<Array<Record<string, unknown>>>('/notifications')
  else {
    const d = await loadMock<{ notifications: Array<Record<string, unknown>> }>('notifications.json')
    rows = d.notifications ?? []
  }
  return hideAdminOnlyNotifications(scopeNotificationsToPersona(rows))
}

/**
 * Access-request notices (raised by /api/access/request-notify) carry another
 * person's name and email and an approval to make: only app admins see them.
 */
function hideAdminOnlyNotifications<T extends Record<string, unknown>>(rows: T[]): T[] {
  if (useModuleEntitlementStore.getState().isAdmin) return rows
  return rows.filter((n) => n.entityType !== 'access_request')
}

// ---------------------------------------------------------------------------
// Baseline Management
// ---------------------------------------------------------------------------
export async function getBaselineModels(projectId?: string): Promise<BaselineModel[]> {
  if (isSapMode()) return sapBackend.getBaselineModels(projectId)
  if (isSupabaseMode()) return sb.getBaselineModels(projectId)
  if (!isMockMode()) return fetchApi<BaselineModel[]>('/baselines/models', { projectId })
  const d = await loadMock<{ baselineModels: BaselineModel[] }>('portfolio/baseline-models.json')
  const all = d.baselineModels ?? []
  return projectId ? all.filter(b => b.projectId === projectId) : all
}

export async function getBaselineWbsSummaries(baselineModelId?: string): Promise<BaselineWbsSummary[]> {
  if (isSupabaseMode()) return sb.getBaselineWbsSummaries(baselineModelId)
  if (!isMockMode()) return fetchApi<BaselineWbsSummary[]>('/baselines/wbs-summaries', { baselineModelId })
  const d = await loadMock<{ baselineWbsSummaries: BaselineWbsSummary[] }>('portfolio/baseline-models.json')
  const all = d.baselineWbsSummaries ?? []
  return baselineModelId ? all.filter(s => s.baselineModelId === baselineModelId) : all
}

export async function getBaselineTimePhaseEntries(baselineModelId?: string): Promise<BaselineTimePhaseEntry[]> {
  if (isSupabaseMode()) return sb.getBaselineTimePhaseEntries(baselineModelId)
  if (!isMockMode()) return fetchApi<BaselineTimePhaseEntry[]>('/baselines/time-phase', { baselineModelId })
  const d = await loadMock<{ baselineTimePhaseEntries: BaselineTimePhaseEntry[] }>('portfolio/baseline-models.json')
  const all = d.baselineTimePhaseEntries ?? []
  return baselineModelId ? all.filter(e => e.baselineModelId === baselineModelId) : all
}

export async function getBurdenRateDecks(projectId?: string): Promise<BurdenRateDeck[]> {
  if (isSupabaseMode()) return sb.getBurdenRateDecks(projectId)
  if (!isMockMode()) return fetchApi<BurdenRateDeck[]>('/baselines/burden-rate-decks', { projectId })
  const d = await loadMock<{ burdenRateDecks: BurdenRateDeck[] }>('portfolio/burden-rate-decks.json')
  const all = d.burdenRateDecks ?? []
  return projectId ? all.filter(r => r.projectId === projectId) : all
}

export async function getResourceCategories(): Promise<ResourceCategory[]> {
  if (isSapMode()) return sapBackend.getResourceCategories()
  if (isSupabaseMode()) return sb.getResourceCategories()
  if (!isMockMode()) return fetchApi<ResourceCategory[]>('/baselines/resource-categories')
  const d = await loadMock<{ resourceCategories: ResourceCategory[] }>('portfolio/resource-categories.json')
  return d.resourceCategories ?? []
}

export async function getScheduleVersions(projectId?: string): Promise<ScheduleVersionSummary[]> {
  if (isSupabaseMode()) return sb.getScheduleVersions(projectId)
  if (!isMockMode()) return fetchApi<ScheduleVersionSummary[]>(`/schedule-versions${projectId ? `?projectId=${projectId}` : ''}`)
  const d = await loadMock<{ scheduleVersions: ScheduleVersionSummary[] }>('schedules/schedule-versions.json')
  const all = d.scheduleVersions ?? []
  return projectId ? all.filter(sv => sv.projectId === projectId) : all
}

export async function getBaselineChangeRequests(projectId?: string, baselineModelId?: string): Promise<BaselineChangeRequest[]> {
  if (isSupabaseMode()) return sb.getBaselineChangeRequests(projectId, baselineModelId)
  if (!isMockMode()) return fetchApi<BaselineChangeRequest[]>(`/baseline-change-requests${projectId ? `?projectId=${projectId}` : ''}`)
  const d = await loadMock<{ baselineChangeRequests: BaselineChangeRequest[] }>('portfolio/baseline-change-requests.json')
  let all = d.baselineChangeRequests ?? []
  if (projectId) all = all.filter(bcr => bcr.projectId === projectId)
  if (baselineModelId) all = all.filter(bcr => bcr.baselineModelId === baselineModelId)
  return all
}

export async function getRevenuePlanEntries(baselineModelId?: string): Promise<RevenuePlanEntry[]> {
  if (isSupabaseMode()) return sb.getRevenuePlanEntries(baselineModelId)
  if (!isMockMode()) return fetchApi<RevenuePlanEntry[]>('/baselines/revenue-plan', { baselineModelId })
  const d = await loadMock<{ revenuePlanEntries: RevenuePlanEntry[] }>('portfolio/revenue-plan-entries.json')
  const all = d.revenuePlanEntries ?? []
  return baselineModelId ? all.filter(e => e.baselineModelId === baselineModelId) : all
}

// ---------------------------------------------------------------------------
// Forecasting
// ---------------------------------------------------------------------------
export async function getProjectForecastConfig(projectId?: string): Promise<ProjectForecastConfig[]> {
  if (isSupabaseMode()) return sb.getProjectForecastConfig(projectId) as unknown as Promise<ProjectForecastConfig[]>
  if (!isMockMode()) return fetchApi<ProjectForecastConfig[]>('/forecasting/config', { projectId })
  const d = await loadMock<{ projectForecastConfigs: ProjectForecastConfig[] }>('forecasting/forecast-config.json')
  const all = d.projectForecastConfigs ?? []
  return projectId ? all.filter(c => c.projectId === projectId) : all
}

export async function getForecastVersions(projectId?: string): Promise<ForecastVersion[]> {
  if (isSapMode()) return sapBackend.getForecastVersions(projectId)
  if (isSupabaseMode()) return sb.getForecastVersions(projectId) as unknown as Promise<ForecastVersion[]>
  if (!isMockMode()) return fetchApi<ForecastVersion[]>('/forecasting/versions', { projectId })
  const d = await loadMock<{ forecastVersions: ForecastVersion[] }>('forecasting/forecast-versions.json')
  const all = d.forecastVersions ?? []
  return projectId ? all.filter(v => v.projectId === projectId) : all
}

/**
 * Create a new forecast version. Persists to Supabase in supabase mode;
 * in mock/api mode the version is returned as-is so the UI can add it
 * optimistically (no mock JSON write path exists).
 */
export async function createForecastVersion(version: ForecastVersion): Promise<ForecastVersion> {
  if (isSupabaseMode()) return sb.createForecastVersion(version) as unknown as Promise<ForecastVersion>
  return version
}

// ---------------------------------------------------------------------------
// Rates Management - labor rates, costing-sheet rate decks, deck assignments.
// Supabase-backed; in mock/api mode reads return [] and writes echo (no mock
// JSON write path), so the page works in every data-source mode.
// ---------------------------------------------------------------------------
export async function getProjectLaborRates(projectId?: string): Promise<import('@/types/rates').LaborRateOrgDiscipline[]> {
  if (isSupabaseMode()) return sb.getProjectLaborRates(projectId) as unknown as Promise<import('@/types/rates').LaborRateOrgDiscipline[]>
  return []
}
export async function upsertProjectLaborRate(rate: import('@/types/rates').LaborRateOrgDiscipline) {
  if (isSupabaseMode()) return sb.upsertProjectLaborRate(rate)
  return rate
}
export async function deleteProjectLaborRate(id: string) {
  if (isSupabaseMode()) return sb.deleteProjectLaborRate(id)
}
export async function getProjectRateDecks(projectId?: string): Promise<import('@/types/rates').CostingSheetVersion[]> {
  if (isSupabaseMode()) return sb.getProjectRateDecks(projectId) as unknown as Promise<import('@/types/rates').CostingSheetVersion[]>
  return []
}
export async function upsertProjectRateDeck(version: import('@/types/rates').CostingSheetVersion) {
  if (isSupabaseMode()) return sb.upsertProjectRateDeck(version)
  return version
}
export async function deleteProjectRateDeck(id: string) {
  if (isSupabaseMode()) return sb.deleteProjectRateDeck(id)
}
export async function getRateDeckAssignments(projectId?: string): Promise<import('@/types/rates').RateDeckAssignment[]> {
  if (isSupabaseMode()) return sb.getRateDeckAssignments(projectId) as unknown as Promise<import('@/types/rates').RateDeckAssignment[]>
  return []
}

// ---------------------------------------------------------------------------
// CAS / DCAA Rates Management (Organizational Management context).
// Org-wide cost waterfall: segments -> rate pools -> cost pools -> cost records.
// ---------------------------------------------------------------------------
type CasSegmentT = import('@/types/casRates').RateSegment
type CasRatePoolT = import('@/types/casRates').RatePool
type CasCostPoolT = import('@/types/casRates').CostPool
type CasCostRecordT = import('@/types/casRates').CostRecord

export async function getCasRateSegments(orgId?: string): Promise<CasSegmentT[]> {
  if (isSupabaseMode()) return sb.getCasRateSegments(orgId) as unknown as Promise<CasSegmentT[]>
  return []
}
export async function upsertCasRateSegment(segment: CasSegmentT): Promise<CasSegmentT> {
  if (isSupabaseMode()) return sb.upsertCasRateSegment(segment)
  return segment
}
export async function deleteCasRateSegment(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCasRateSegment(id)
}

export async function getCasRatePools(orgId?: string): Promise<CasRatePoolT[]> {
  if (isSupabaseMode()) return sb.getCasRatePools(orgId) as unknown as Promise<CasRatePoolT[]>
  return []
}
export async function upsertCasRatePool(pool: CasRatePoolT): Promise<CasRatePoolT> {
  if (isSupabaseMode()) return sb.upsertCasRatePool(pool)
  return pool
}
export async function deleteCasRatePool(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCasRatePool(id)
}

export async function getCasCostPools(orgId?: string): Promise<CasCostPoolT[]> {
  if (isSupabaseMode()) return sb.getCasCostPools(orgId) as unknown as Promise<CasCostPoolT[]>
  return []
}
export async function upsertCasCostPool(pool: CasCostPoolT): Promise<CasCostPoolT> {
  if (isSupabaseMode()) return sb.upsertCasCostPool(pool)
  return pool
}
export async function deleteCasCostPool(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCasCostPool(id)
}

export async function getCasCostRecords(orgId?: string): Promise<CasCostRecordT[]> {
  if (isSupabaseMode()) return sb.getCasCostRecords(orgId) as unknown as Promise<CasCostRecordT[]>
  return []
}
export async function upsertCasCostRecord(rec: CasCostRecordT): Promise<CasCostRecordT> {
  if (isSupabaseMode()) return sb.upsertCasCostRecord(rec)
  return rec
}
export async function upsertRateDeckAssignment(a: import('@/types/rates').RateDeckAssignment) {
  if (isSupabaseMode()) return sb.upsertRateDeckAssignment(a)
  return a
}
export async function deleteRateDeckAssignment(id: string) {
  if (isSupabaseMode()) return sb.deleteRateDeckAssignment(id)
}
export async function getProjectLoadedResources(projectId: string): Promise<Record<string, unknown>[]> {
  if (isSupabaseMode()) return sb.getProjectLoadedResources(projectId)
  return []
}

export async function getForecastWbsSummaries(forecastVersionId?: string): Promise<ForecastWbsSummary[]> {
  if (isSupabaseMode()) return sb.getForecastWbsSummaries(forecastVersionId) as unknown as Promise<ForecastWbsSummary[]>
  if (!isMockMode()) return fetchApi<ForecastWbsSummary[]>('/forecasting/wbs-summaries', { forecastVersionId })
  const d = await loadMock<{ forecastWbsSummaries: ForecastWbsSummary[] }>('forecasting/forecast-wbs-summaries.json')
  const all = d.forecastWbsSummaries ?? []
  return forecastVersionId ? all.filter(s => s.forecastVersionId === forecastVersionId) : all
}

export async function getForecastResourceLines(forecastVersionId?: string, costNodeId?: string): Promise<ForecastResourceLine[]> {
  if (isSupabaseMode()) return sb.getForecastResourceLines(forecastVersionId, costNodeId) as unknown as Promise<ForecastResourceLine[]>
  if (!isMockMode()) return fetchApi<ForecastResourceLine[]>('/forecasting/resource-lines', { forecastVersionId, costNodeId })
  const d = await loadMock<{ forecastResourceLines: ForecastResourceLine[] }>('forecasting/forecast-resource-lines.json')
  let all = d.forecastResourceLines ?? []
  if (forecastVersionId) all = all.filter(l => l.forecastVersionId === forecastVersionId)
  if (costNodeId) all = all.filter(l => l.costNodeId === costNodeId)
  return all
}

export async function getForecastTimePhaseEntries(forecastVersionId?: string, costNodeId?: string): Promise<ForecastTimePhaseEntry[]> {
  if (isSupabaseMode()) return sb.getForecastTimePhaseEntries(forecastVersionId, costNodeId) as unknown as Promise<ForecastTimePhaseEntry[]>
  if (!isMockMode()) return fetchApi<ForecastTimePhaseEntry[]>('/forecasting/time-phase', { forecastVersionId, costNodeId })
  const d = await loadMock<{ forecastTimePhaseEntries: ForecastTimePhaseEntry[] }>('forecasting/forecast-time-phase.json')
  let all = d.forecastTimePhaseEntries ?? []
  if (forecastVersionId) all = all.filter(e => e.forecastVersionId === forecastVersionId)
  // costNodeId is an optional extra field present on JSONB seed rows but
  // not on the typed ForecastTimePhaseEntry interface - cast for the
  // runtime filter so the legacy cost-node binding still works.
  if (costNodeId) all = all.filter(e => (e as unknown as { costNodeId?: string }).costNodeId === costNodeId)
  return all
}

export async function saveForecastResourceLines(lines: ForecastResourceLine[]): Promise<void> {
  if (!isSupabaseMode()) return
  await sb.upsertForecastResourceLines(lines)
}

export async function saveForecastTimePhaseEntries(entries: ForecastTimePhaseEntry[]): Promise<void> {
  if (!isSupabaseMode()) return
  await sb.upsertForecastTimePhaseEntries(entries)
}

export async function getRevenueForecastLines(forecastVersionId?: string, costNodeId?: string): Promise<RevenueForecastLine[]> {
  if (isSupabaseMode()) return sb.getRevenueForecastLines(forecastVersionId, costNodeId) as unknown as Promise<RevenueForecastLine[]>
  if (!isMockMode()) return fetchApi<RevenueForecastLine[]>('/forecasting/revenue-lines', { forecastVersionId, costNodeId })
  const d = await loadMock<{ revenueForecastLines: RevenueForecastLine[] }>('forecasting/forecast-revenue.json')
  let all = d.revenueForecastLines ?? []
  if (forecastVersionId) all = all.filter(l => l.forecastVersionId === forecastVersionId)
  if (costNodeId) all = all.filter(l => (l as unknown as { costNodeId?: string }).costNodeId === costNodeId)
  return all
}

export async function getRevenueTimePhaseEntries(forecastVersionId?: string, costNodeId?: string): Promise<RevenueTimePhaseEntry[]> {
  if (isSupabaseMode()) return sb.getRevenueTimePhaseEntries(forecastVersionId, costNodeId) as unknown as Promise<RevenueTimePhaseEntry[]>
  if (!isMockMode()) return fetchApi<RevenueTimePhaseEntry[]>('/forecasting/revenue-time-phase', { forecastVersionId, costNodeId })
  const d = await loadMock<{ revenueTimePhaseEntries: RevenueTimePhaseEntry[] }>('forecasting/forecast-revenue.json')
  let all = d.revenueTimePhaseEntries ?? []
  if (forecastVersionId) all = all.filter(e => e.forecastVersionId === forecastVersionId)
  if (costNodeId) all = all.filter(e => (e as unknown as { costNodeId?: string }).costNodeId === costNodeId)
  return all
}

export async function getForecastSubmissions(forecastVersionId?: string): Promise<ForecastSubmission[]> {
  if (isSupabaseMode()) return sb.getForecastSubmissions(forecastVersionId) as unknown as Promise<ForecastSubmission[]>
  if (!isMockMode()) return fetchApi<ForecastSubmission[]>('/forecasting/submissions', { forecastVersionId })
  const d = await loadMock<{ forecastSubmissions: ForecastSubmission[] }>('forecasting/forecast-submissions.json')
  const all = d.forecastSubmissions ?? []
  return forecastVersionId ? all.filter(s => s.forecastVersionId === forecastVersionId) : all
}

export async function getActualsSyncRecords(forecastVersionId?: string): Promise<ActualsSyncRecord[]> {
  if (isSupabaseMode()) return sb.getActualsSyncRecords(forecastVersionId) as unknown as Promise<ActualsSyncRecord[]>
  if (!isMockMode()) return fetchApi<ActualsSyncRecord[]>('/forecasting/actuals-sync', { forecastVersionId })
  const d = await loadMock<{ actualsSyncRecords: ActualsSyncRecord[] }>('forecasting/forecast-submissions.json')
  const all = d.actualsSyncRecords ?? []
  return forecastVersionId ? all.filter(r => r.forecastVersionId === forecastVersionId) : all
}

export async function getMaterialForecastLines(forecastVersionId?: string): Promise<MaterialForecastLine[]> {
  if (isSupabaseMode()) return sb.getMaterialForecastLines(forecastVersionId) as unknown as Promise<MaterialForecastLine[]>
  if (!isMockMode()) return fetchApi<MaterialForecastLine[]>('/forecasting/materials', { forecastVersionId })
  const d = await loadMock<{ materialForecastLines: MaterialForecastLine[] }>('forecasting/forecast-materials.json')
  const all = d.materialForecastLines ?? []
  return forecastVersionId ? all.filter(l => l.forecastVersionId === forecastVersionId) : all
}

export async function getOdcForecastLines(forecastVersionId?: string): Promise<OdcForecastLine[]> {
  if (isSupabaseMode()) return sb.getOdcForecastLines(forecastVersionId) as unknown as Promise<OdcForecastLine[]>
  if (!isMockMode()) return fetchApi<OdcForecastLine[]>('/forecasting/odc-lines', { forecastVersionId })
  const d = await loadMock<{ odcForecastLines: OdcForecastLine[] }>('forecasting/forecast-odc.json')
  const all = d.odcForecastLines ?? []
  return forecastVersionId ? all.filter(l => l.forecastVersionId === forecastVersionId) : all
}

// Material / ODC forecast line persistence. Supabase-backed; echo in mock/api.
export async function upsertMaterialForecastLine(line: MaterialForecastLine): Promise<MaterialForecastLine> {
  if (isSupabaseMode()) await sb.upsertMaterialForecastLine(line as unknown as Record<string, unknown>)
  return line
}
export async function deleteMaterialForecastLine(id: string): Promise<void> {
  if (isSupabaseMode()) await sb.deleteMaterialForecastLine(id)
}
export async function upsertOdcForecastLine(line: OdcForecastLine): Promise<OdcForecastLine> {
  if (isSupabaseMode()) await sb.upsertOdcForecastLine(line as unknown as Record<string, unknown>)
  return line
}
export async function deleteOdcForecastLine(id: string): Promise<void> {
  if (isSupabaseMode()) await sb.deleteOdcForecastLine(id)
}

export async function getOdcCategories(): Promise<OdcCategory[]> {
  if (isSupabaseMode()) return sb.getOdcCategories() as unknown as Promise<OdcCategory[]>
  if (!isMockMode()) return fetchApi<OdcCategory[]>('/forecasting/odc-categories')
  const d = await loadMock<{ odcCategories: OdcCategory[] }>('forecasting/forecast-odc.json')
  return d.odcCategories ?? []
}

export async function getCapitalAssetForecastLines(forecastVersionId?: string): Promise<CapitalAssetForecastLine[]> {
  if (isSupabaseMode()) return sb.getCapitalAssetForecastLines(forecastVersionId) as unknown as Promise<CapitalAssetForecastLine[]>
  if (!isMockMode()) return fetchApi<CapitalAssetForecastLine[]>('/forecasting/capital-assets', { forecastVersionId })
  const d = await loadMock<{ capitalAssetForecastLines: CapitalAssetForecastLine[] }>('forecasting/forecast-capital-assets.json')
  const all = d.capitalAssetForecastLines ?? []
  return forecastVersionId ? all.filter(l => l.forecastVersionId === forecastVersionId) : all
}

export async function getWorkAuthorizations(projectId?: string): Promise<WorkAuthorization[]> {
  if (isSupabaseMode()) return sb.getWorkAuthorizations(projectId) as unknown as Promise<WorkAuthorization[]>
  if (!isMockMode()) return fetchApi<WorkAuthorization[]>('/forecasting/work-authorizations', { projectId })
  const d = await loadMock<{ workAuthorizations: WorkAuthorization[] }>('forecasting/forecast-work-auth.json')
  const all = d.workAuthorizations ?? []
  return projectId ? all.filter(a => a.projectId === projectId) : all
}

// ---------------------------------------------------------------------------
// Work Authorization staffing (migration 00342). Supabase-only feature: there
// is no mock JSON for the assignment store, so non-supabase modes return empty
// reads and writes throw. The demand-slot read model lives in
// components/forecasting/resourceDemandStaffing.ts (it queries Supabase directly,
// mirroring laborForecastPull).
// ---------------------------------------------------------------------------

export async function getWorkAuthorizationAssignments(projectId?: string): Promise<import('@/types/forecasting').WorkAuthorizationAssignment[]> {
  if (isSupabaseMode()) return sb.getWorkAuthorizationAssignments(projectId) as unknown as Promise<import('@/types/forecasting').WorkAuthorizationAssignment[]>
  return []
}

export async function upsertWorkAuthorizationAssignment(a: import('@/types/forecasting').WorkAuthorizationAssignment): Promise<import('@/types/forecasting').WorkAuthorizationAssignment> {
  if (!isSupabaseMode()) throw new Error('Work Authorization staffing requires the Supabase data source.')
  return sb.upsertWorkAuthorizationAssignment(a) as unknown as Promise<import('@/types/forecasting').WorkAuthorizationAssignment>
}

export async function deleteWorkAuthorizationAssignmentByLine(planLineId: string): Promise<void> {
  if (!isSupabaseMode()) throw new Error('Work Authorization staffing requires the Supabase data source.')
  await sb.deleteWorkAuthorizationAssignmentByLine(planLineId)
}

export async function getEacCycles(projectId?: string): Promise<EacCycle[]> {
  if (isSupabaseMode()) return sb.getEacCycles(projectId) as unknown as Promise<EacCycle[]>
  if (!isMockMode()) return fetchApi<EacCycle[]>('/forecasting/eac-cycles', { projectId })
  const d = await loadMock<{ eacCycles: EacCycle[] }>('forecasting/forecast-submissions.json')
  const all = d.eacCycles ?? []
  return projectId ? all.filter(c => c.projectId === projectId) : all
}

export async function getEacSubmissionsByProject(projectId: string): Promise<EacSubmission[]> {
  if (isSupabaseMode()) return sb.getEacSubmissionsByProject(projectId) as unknown as Promise<EacSubmission[]>
  if (!isMockMode()) return fetchApi<EacSubmission[]>('/forecasting/eac-submissions', { projectId })
  const d = await loadMock<{ eacSubmissions: EacSubmission[] }>('forecasting/forecast-submissions.json')
  const all = d.eacSubmissions ?? []
  // Mock data is project-scoped already; if not, this returns the whole list
  return all
}

// â"€â"€â"€ EAC write actions â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
// Mock + REST modes return a synthetic optimistic record so the UI can update
// without a full refetch. Real persistence only fires in Supabase mode.

export async function submitEacSubmission(
  submissionId: string,
  submittedBy: string,
  submittedByName: string,
): Promise<{ ok: true }> {
  if (isSupabaseMode()) {
    await sb.submitEacSubmission(submissionId, submittedBy, submittedByName)
  }
  return { ok: true }
}

export async function approveEacApprovalStep(
  approvalId: string,
  submissionId: string,
  isLastStep: boolean,
  comments?: string,
): Promise<{ ok: true; nextStatus: 'Approved' | 'UnderReview' }> {
  if (isSupabaseMode()) {
    await sb.approveEacApprovalStep(approvalId, submissionId, isLastStep, comments)
  }
  return { ok: true, nextStatus: isLastStep ? 'Approved' : 'UnderReview' }
}

export async function rejectEacApprovalStep(
  approvalId: string,
  submissionId: string,
  comments?: string,
): Promise<{ ok: true }> {
  if (isSupabaseMode()) {
    await sb.rejectEacApprovalStep(approvalId, submissionId, comments)
  }
  return { ok: true }
}

export async function requestEacRevision(
  approvalId: string,
  submissionId: string,
  comments?: string,
): Promise<{ ok: true }> {
  if (isSupabaseMode()) {
    await sb.requestEacRevision(approvalId, submissionId, comments)
  }
  return { ok: true }
}

// ---------------------------------------------------------------------------
// Tasks by project (composite)
// ---------------------------------------------------------------------------
export async function getTasksByProject(projectId: string) {
  if (isSupabaseMode()) return sb.getTasksByProject(projectId)
  if (!isMockMode()) return fetchApi<Task[]>(`/tasks/by-project/${projectId}`)
  const wbs = await getWbsElements(projectId)
  const wbsIds = wbs.map(w => w.id)
  const d = await loadMock<{ tasks: Task[] }>('tasks.json')
  const all = d.tasks ?? []
  return all.filter(t => wbsIds.includes(t.wbsElementId))
}

// ---------------------------------------------------------------------------
// SAP Integration
// ---------------------------------------------------------------------------

export async function getSapSystems(): Promise<SapSystemConfig[]> {
  if (isSupabaseMode()) return sapSb.getSapSystems()
  if (!isMockMode()) return fetchApi<SapSystemConfig[]>('/sap/systems')
  return sapMock.getSapSystems()
}

export async function getSapSystemById(id: string): Promise<SapSystemConfig | null> {
  if (isSupabaseMode()) return sapSb.getSapSystemById(id)
  if (!isMockMode()) return fetchApi<SapSystemConfig | null>(`/sap/systems/${id}`)
  return sapMock.getSapSystemById(id)
}

export async function getSapIntegrationProfiles(filters?: { projectId?: string; portfolioId?: string }): Promise<SapIntegrationProfile[]> {
  if (isSupabaseMode()) return sapSb.getIntegrationProfiles(filters)
  if (!isMockMode()) return fetchApi<SapIntegrationProfile[]>('/sap/profiles', { projectId: filters?.projectId, portfolioId: filters?.portfolioId })
  return sapMock.getIntegrationProfiles(filters)
}

export async function getSapIntegrationProfileById(id: string): Promise<SapIntegrationProfile | null> {
  if (isSupabaseMode()) return sapSb.getIntegrationProfileById(id)
  if (!isMockMode()) return fetchApi<SapIntegrationProfile | null>(`/sap/profiles/${id}`)
  return sapMock.getIntegrationProfileById(id)
}

export async function getSapSyncJobs(profileId?: string, domain?: SyncDomain): Promise<SapSyncJob[]> {
  if (isSupabaseMode()) return sapSb.getSyncJobs(profileId, domain)
  if (!isMockMode()) return fetchApi<SapSyncJob[]>('/sap/sync-jobs', { profileId, domain })
  return sapMock.getSyncJobs(profileId, domain)
}

/** #63 - post approved timesheet labor to SAP (mock-sync demo; no live SAP). */
export async function postTimesheetLaborToSap(profileId: string, user: string) {
  return sapMock.postTimesheetLaborToSap(profileId, user)
}

export async function getSapSyncedActuals(projectId: string, period?: string): Promise<SyncedActualsSummary[]> {
  if (isSupabaseMode()) return sapSb.getSyncedActualsSummaries(projectId, period)
  if (!isMockMode()) return fetchApi<SyncedActualsSummary[]>('/sap/synced-actuals', { projectId, period })
  // Mock: return empty â€" synced actuals are produced by the sync engine
  return []
}

export async function getSapIntegrationDashboard(profileId: string): Promise<SapIntegrationDashboard | null> {
  if (isSupabaseMode()) return sapSb.getIntegrationDashboard(profileId)
  if (!isMockMode()) return fetchApi<SapIntegrationDashboard | null>(`/sap/dashboard/${profileId}`)
  return sapMock.getIntegrationDashboard(profileId)
}

export async function getSapCostElementMasters(profileId: string): Promise<SapCostElementMaster[]> {
  if (isSupabaseMode()) return sapSb.getCostElementMasters(profileId)
  if (!isMockMode()) return fetchApi<SapCostElementMaster[]>('/sap/cost-elements', { profileId })
  return sapMock.getCostElementMasters(profileId)
}

export async function getSapWbsElementMasters(profileId: string): Promise<SapWbsElementMaster[]> {
  if (isSupabaseMode()) return sapSb.getWbsElementMasters(profileId)
  if (!isMockMode()) return fetchApi<SapWbsElementMaster[]>('/sap/wbs-masters', { profileId })
  return sapMock.getWbsElementMasters(profileId)
}

// ---------------------------------------------------------------------------
// Cost Management Studio - universal ledger (migration 00432)
// Supabase-only: the studio has no mock branch. Reads return empty in other
// data-source modes rather than inventing rows.
// ---------------------------------------------------------------------------
type CmLineT = import('@/types/costManagement').CmLedgerLine
type CmSyncT = import('@/types/costManagement').CmLedgerSync
type CmObjectT = import('@/types/costManagement').CmCoObject
type CmQueryT = import('@/types/costManagement').LedgerQuery

export async function getCmLedgerLines(q: CmQueryT = {}): Promise<CmLineT[]> {
  if (isSupabaseMode()) return sb.getCmLedgerLines(q)
  return []
}
export async function getCmLedgerLineCount(q: CmQueryT = {}): Promise<number> {
  if (isSupabaseMode()) return sb.getCmLedgerLineCount(q)
  return 0
}
export async function upsertCmLedgerLines(lines: CmLineT[]): Promise<number> {
  if (isSupabaseMode()) return sb.upsertCmLedgerLines(lines)
  return 0
}
export async function deleteCmLedgerLinesByRun(runId: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCmLedgerLinesByRun(runId)
}
export async function getCmLedgerSyncs(orgId?: string): Promise<CmSyncT[]> {
  if (isSupabaseMode()) return sb.getCmLedgerSyncs(orgId)
  return []
}
export async function upsertCmLedgerSync(sync: CmSyncT): Promise<CmSyncT> {
  if (isSupabaseMode()) return sb.upsertCmLedgerSync(sync)
  return sync
}
export async function getCmCoObjects(orgId?: string): Promise<CmObjectT[]> {
  if (isSupabaseMode()) return sb.getCmCoObjects(orgId)
  return []
}
export async function upsertCmCoObjects(objects: CmObjectT[]): Promise<number> {
  if (isSupabaseMode()) return sb.upsertCmCoObjects(objects)
  return 0
}
export async function upsertCmCoObject(o: CmObjectT): Promise<CmObjectT> {
  if (isSupabaseMode()) return sb.upsertCmCoObject(o)
  return o
}

// ---------------------------------------------------------------------------
// Cost Management Studio - pool membership + rate segment rules (00434)
// ---------------------------------------------------------------------------
type CasRuleT = import('@/lib/costmgmt/segmentRules').CasSegmentRule
type CasAssignT = import('@/lib/costmgmt/segmentRules').CasObjectAssignment

export async function getCasSegmentRules(orgId?: string): Promise<CasRuleT[]> {
  if (isSupabaseMode()) return sb.getCasSegmentRules(orgId)
  return []
}
export async function upsertCasSegmentRule(rule: CasRuleT): Promise<CasRuleT> {
  if (isSupabaseMode()) return sb.upsertCasSegmentRule(rule)
  return rule
}
export async function deleteCasSegmentRule(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCasSegmentRule(id)
}
export async function getCasObjectAssignments(orgId?: string): Promise<CasAssignT[]> {
  if (isSupabaseMode()) return sb.getCasObjectAssignments(orgId)
  return []
}
export async function upsertCasObjectAssignment(a: CasAssignT): Promise<CasAssignT> {
  if (isSupabaseMode()) return sb.upsertCasObjectAssignment(a)
  return a
}
export async function deleteCasObjectAssignment(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCasObjectAssignment(id)
}

// ---------------------------------------------------------------------------
// Cost Management Studio - allocations (00436)
// ---------------------------------------------------------------------------
type CmAllocRuleT = import('@/lib/costmgmt/allocationEngine').AllocationRule
type CmAllocRunT = import('@/services/costmgmt/allocationService').AllocationRun

export async function getCmAllocationRules(orgId?: string): Promise<CmAllocRuleT[]> {
  if (isSupabaseMode()) return sb.getCmAllocationRules(orgId)
  return []
}
export async function upsertCmAllocationRule(rule: CmAllocRuleT): Promise<CmAllocRuleT> {
  if (isSupabaseMode()) return sb.upsertCmAllocationRule(rule)
  return rule
}
export async function deleteCmAllocationRule(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCmAllocationRule(id)
}
export async function getCmAllocationRuns(orgId?: string): Promise<CmAllocRunT[]> {
  if (isSupabaseMode()) return sb.getCmAllocationRuns(orgId)
  return []
}
export async function upsertCmAllocationRun(run: CmAllocRunT): Promise<CmAllocRunT> {
  if (isSupabaseMode()) return sb.upsertCmAllocationRun(run)
  return run
}

// ---------------------------------------------------------------------------
// Cost Management Studio - burden rates (00437)
// ---------------------------------------------------------------------------
type CmBurdenBaseT = import('@/lib/costmgmt/burdenEngine').BurdenBase
type CmBurdenDefT = import('@/lib/costmgmt/burdenEngine').BurdenDef
type CmBurdenRunT = import('@/services/costmgmt/burdenService').BurdenRun

export async function getCmBurdenBases(orgId?: string): Promise<CmBurdenBaseT[]> {
  if (isSupabaseMode()) return sb.getCmBurdenBases(orgId)
  return []
}
export async function upsertCmBurdenBase(b: CmBurdenBaseT): Promise<CmBurdenBaseT> {
  if (isSupabaseMode()) return sb.upsertCmBurdenBase(b)
  return b
}
export async function deleteCmBurdenBase(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCmBurdenBase(id)
}
export async function getCmBurdenDefs(orgId?: string): Promise<CmBurdenDefT[]> {
  if (isSupabaseMode()) return sb.getCmBurdenDefs(orgId)
  return []
}
export async function upsertCmBurdenDef(d: CmBurdenDefT): Promise<CmBurdenDefT> {
  if (isSupabaseMode()) return sb.upsertCmBurdenDef(d)
  return d
}
export async function deleteCmBurdenDef(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCmBurdenDef(id)
}
export async function getCmBurdenRuns(orgId?: string): Promise<CmBurdenRunT[]> {
  if (isSupabaseMode()) return sb.getCmBurdenRuns(orgId)
  return []
}
export async function upsertCmBurdenRun(run: CmBurdenRunT): Promise<CmBurdenRunT> {
  if (isSupabaseMode()) return sb.upsertCmBurdenRun(run)
  return run
}

// ---------------------------------------------------------------------------
// Cost Management Studio - cost transfers (00439)
// ---------------------------------------------------------------------------
type CmTransferT = import('@/services/costmgmt/transferService').CostTransfer

export async function getCmTransfers(orgId?: string): Promise<CmTransferT[]> {
  if (isSupabaseMode()) return sb.getCmTransfers(orgId)
  return []
}
export async function upsertCmTransfer(t: CmTransferT): Promise<CmTransferT> {
  if (isSupabaseMode()) return sb.upsertCmTransfer(t)
  return t
}
export async function deleteCmTransfer(id: string): Promise<void> {
  if (isSupabaseMode()) return sb.deleteCmTransfer(id)
}

// ---------------------------------------------------------------------------
// Cost Management Studio - incurred cost reporting (00440)
// ---------------------------------------------------------------------------
type CmIcrSubT = import('@/services/costmgmt/icrService').IcrSubmission
type CmIcrInputT = import('@/services/costmgmt/icrService').IcrInputRow

export async function getCmIcrSubmissions(orgId?: string): Promise<CmIcrSubT[]> {
  if (isSupabaseMode()) return sb.getCmIcrSubmissions(orgId)
  return []
}
export async function upsertCmIcrSubmission(s: CmIcrSubT): Promise<CmIcrSubT> {
  if (isSupabaseMode()) return sb.upsertCmIcrSubmission(s)
  return s
}
export async function getCmIcrInputs(orgId?: string): Promise<CmIcrInputT[]> {
  if (isSupabaseMode()) return sb.getCmIcrInputs(orgId)
  return []
}
export async function upsertCmIcrInput(row: CmIcrInputT): Promise<CmIcrInputT> {
  if (isSupabaseMode()) return sb.upsertCmIcrInput(row)
  return row
}

// ---------------------------------------------------------------------------
// Agile Delivery (Hybrid Waterfall/Agile feature - P0)
// Read fetchers through the mock/api/supabase funnel. Writes go through
// agileWriteService (Supabase) directly, like scheduleWriteService.
// ---------------------------------------------------------------------------
export async function getWorkItems(projectId: string, projectCode?: string): Promise<WorkItem[]> {
  if (isSupabaseMode()) return agile.getWorkItems(projectId, projectCode)
  if (!isMockMode()) return fetchApi<WorkItem[]>('/work-items', { projectId })
  const d = await loadMock<{ workItems: WorkItem[] }>('work_items.json')
  return (d.workItems ?? []).filter((w) => w.projectId === projectId)
}

export async function getAgileBoard(projectId: string, boardId?: string): Promise<Board | null> {
  if (isSupabaseMode()) return agile.getBoard(projectId, boardId)
  if (!isMockMode()) return fetchApi<Board | null>('/agile-boards', { projectId, boardId })
  const d = await loadMock<{ boards: Board[] }>('agile_boards.json')
  const boards = (d.boards ?? []).filter((b) => b.projectId === projectId)
  if (boards.length === 0) return null
  if (boardId) return boards.find((b) => b.id === boardId) ?? null
  return boards.find((b) => b.isDefault) ?? boards[0] ?? null
}

export async function getAgileBoards(projectId: string): Promise<Board[]> {
  if (isSupabaseMode()) return agile.getBoards(projectId)
  if (!isMockMode()) return fetchApi<Board[]>('/agile-boards/all', { projectId })
  const d = await loadMock<{ boards: Board[] }>('agile_boards.json')
  return (d.boards ?? []).filter((b) => b.projectId === projectId)
}

export async function getSprints(projectId: string): Promise<Sprint[]> {
  if (isSupabaseMode()) return agile.getSprints(projectId)
  if (!isMockMode()) return fetchApi<Sprint[]>('/agile-sprints', { projectId })
  const d = await loadMock<{ sprints: Sprint[] }>('agile_sprints.json')
  return (d.sprints ?? []).filter((s) => s.projectId === projectId)
}

export async function getProgramIncrements(projectId: string): Promise<ProgramIncrement[]> {
  if (isSupabaseMode()) return agile.getProgramIncrements(projectId)
  if (!isMockMode()) return fetchApi<ProgramIncrement[]>('/agile-program-increments', { projectId })
  const d = await loadMock<{ programIncrements: ProgramIncrement[] }>('agile_program_increments.json')
  return (d.programIncrements ?? []).filter((p) => p.projectId === projectId)
}

export async function getWorkItemLinks(projectId: string): Promise<WorkItemLink[]> {
  if (isSupabaseMode()) return agile.getWorkItemLinks(projectId)
  if (!isMockMode()) return fetchApi<WorkItemLink[]>('/work-item-links', { projectId })
  return []
}

export async function getWorkItemComments(workItemId: string): Promise<WorkItemComment[]> {
  if (isSupabaseMode()) return agile.getWorkItemComments(workItemId)
  if (!isMockMode()) return fetchApi<WorkItemComment[]>('/work-item-comments', { workItemId })
  return []
}

export async function getWorkItemActivity(workItemId: string): Promise<WorkItemActivity[]> {
  if (isSupabaseMode()) return agile.getWorkItemActivity(workItemId)
  if (!isMockMode()) return fetchApi<WorkItemActivity[]>('/work-item-activity', { workItemId })
  return []
}

export async function getAgileMetricSnapshots(opts: { sprintId?: string; boardId?: string; projectId?: string }): Promise<AgileMetricSnapshot[]> {
  if (isSupabaseMode()) return agile.getMetricSnapshots(opts)
  if (!isMockMode()) return fetchApi<AgileMetricSnapshot[]>('/agile-metric-snapshots', { sprintId: opts.sprintId, boardId: opts.boardId, projectId: opts.projectId })
  return []
}

/**
 * Optional Implementation Delivery adapter (P7): read an implementation
 * project's `impl_features` (and their dependencies) as read-only agile
 * WorkItems / WorkItemLinks, so an implementation project can be rendered on the
 * general Roadmap without migrating its data onto the new model. The heavy
 * implementation service + adapter are imported lazily to avoid pulling the
 * whole implementation module into every agile page. Returns empty on any error
 * or when the project has no implementation features.
 */
export async function getImplFeaturesAsWorkItems(
  projectId: string,
): Promise<{ items: WorkItem[]; links: WorkItemLink[] }> {
  try {
    const [impl, adapter] = await Promise.all([
      import('@/services/implementationService'),
      import('@/services/implFeaturesAdapter'),
    ])
    const bundle = await impl.getImplementationBundle(projectId)
    return {
      items: adapter.adaptImplFeaturesToWorkItems(bundle.features ?? []),
      links: adapter.adaptImplDependenciesToLinks(bundle.dependencies ?? []),
    }
  } catch {
    return { items: [], links: [] }
  }
}
